import type {
  EntityPreview,
  EditorCommand,
  CommandSource,
} from "@dxf-ai-editor/contracts";
import type {
  DrawingDocument,
  DrawingEntity,
  EntityColor,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  coerceColor,
  colorToLegacyString,
  serializeColor,
  readViewportOverrides,
  detachInvalidatedViewportClipObjects,
  THICKNESS_BEARING_ENTITY_TYPES,
  mtextUniformScalePreservesLayoutShape,
} from "@dxf-ai-editor/drawing-model";
import type { MtextLayoutShapeInput } from "@dxf-ai-editor/drawing-model";

import { MIRROR_AXIS_DEGENERATE_LENSQ } from "./geometryTransforms";
import {
  NO_CLIP_HANDLE,
  VIEWPORT_NONRECT_CLIP_BIT,
} from "./viewportClipInvalidate";
import { resolveVpclipBoundaryPolygon } from "./viewportCommands";
import { hasGeometryInverseFamily } from "./geometryInverseFamilies";

import {
  buildSimpleGeometryPreviewEntity,
  buildLwpolylinePreviewEntity,
  buildTextPreviewEntity,
  buildMtextPreviewEntity,
  buildDimensionPreviewEntity,
  buildMultileaderPreviewEntity,
  buildRevisionCloudPreviewEntity,
  buildHatchPreviewEntity,
  buildBlockInsertPreviewEntity,
  buildViewportPreviewEntity,
  buildEllipsePreviewEntity,
  type SimpleGeometryPointLike,
  type SimpleGeometryPoint,
  type SimpleLineEntity,
  type SimplePointEntity,
  type SimpleCircleEntity,
  type SimpleArcEntity,
  type SimpleEllipseEntity,
  type SimpleLwpolylineEntity,
  type SimpleTextEntity,
  type SimpleMtextEntity,
  type SimpleDimensionEntity,
  type SimpleMultileaderEntity,
  type SimpleRevisionCloudEntity,
  type SimpleHatchEntity,
  type SimpleBlockInsertEntity,
  type SimpleViewportEntity,
} from "./simpleGeometry";

/**
 * The MTEXT fields SCALE's `computeInverse` reads to decide whether the
 * forward pass will CLEAR groups 42/43 (@codex PR #794 round 16 P2, #804).
 *
 * `MtextLayoutShapeInput` (the predicate's OWN input type) plus the two
 * measurement halves, because `findEntitiesByIds` hands back loosely-typed
 * document rows — the same structural shape the sibling Block-attribute
 * capture below uses, for the same reason. It deliberately adds NO
 * caller-side defaults: the predicate normalises its own input against
 * `mtextEntitySchema`, so this site and `scaleEntityRadii` run the identical
 * code path and cannot disagree about which entities get cleared.
 */
interface MtextMeasurementCapture extends MtextLayoutShapeInput {
  readonly type?: string;
  readonly horizontalWidth?: number;
  readonly verticalHeight?: number;
}

/**
 * CECOLOR — the current colour stamped onto a newly-created entity (optional;
 * the dispatch boundary supplies it from `cecolorStore`). When present, the
 * preview's `Color` property bag is set to `colorToLegacyString(color)` so the
 * shell preview entity matches the typed `DrawingDocument` entity colour. Absent
 * → the preview keeps its ByLayer default (legacy / tests).
 */
type CreateColorInput = { color?: EntityColor };

/**
 * Build the preview-options `properties` bag carrying the stamped colour (or an
 * empty object when no colour was supplied — preserving the ByLayer default the
 * preview derivers already apply). One-truth helper for the six create-* preview
 * builders so the Color projection is identical across them.
 */
function ceColorPreviewProperties(
  color: EntityColor | undefined,
): Record<string, string> {
  return color !== undefined ? { Color: colorToLegacyString(color) } : {};
}

type CreateLineInput = CreateColorInput & {
  nextId: string;
  spaceId: string;
  start: SimpleGeometryPointLike;
  end: SimpleGeometryPointLike;
};

type CreatePointInput = CreateColorInput & {
  nextId: string;
  spaceId: string;
  location: SimpleGeometryPointLike;
  rotation: number;
};

type CreateCircleInput = CreateColorInput & {
  nextId: string;
  spaceId: string;
  center: SimpleGeometryPointLike;
  radius: number;
};

type CreateArcInput = CreateColorInput & {
  nextId: string;
  spaceId: string;
  center: SimpleGeometryPointLike;
  radius: number;
  startAngle: number;
  endAngle: number;
};

type CreateEllipseInput = CreateColorInput & {
  nextId: string;
  spaceId: string;
  center: SimpleGeometryPoint;
  /** DXF-relative major-axis vector (group codes 11/21). */
  majorAxisEndpoint: SimpleGeometryPoint;
  /** Minor / major ratio in (0, 1]. */
  ratio: number;
  /** Default 0 for full ellipse. */
  startParam?: number;
  /** Default 2π for full ellipse. */
  endParam?: number;
};

type GeometryMutationResult = {
  entity: EntityPreview;
  summary: string;
};

export function createLineEntity(
  input: CreateLineInput,
): GeometryMutationResult {
  const entity: SimpleLineEntity = {
    id: input.nextId,
    type: "Line",
    spaceId: input.spaceId,
    start: {
      x: input.start.x,
      y: input.start.y,
    },
    end: {
      x: input.end.x,
      y: input.end.y,
    },
  };

  return {
    entity: buildSimpleGeometryPreviewEntity(entity, {
      summary: "Simple line created through the geometry command core.",
      properties: ceColorPreviewProperties(input.color),
    }),
    summary: `LINE created from ${input.start.x},${input.start.y} to ${input.end.x},${input.end.y}`,
  };
}

export function createPointEntity(
  input: CreatePointInput,
): GeometryMutationResult {
  const entity: SimplePointEntity = {
    id: input.nextId,
    type: "Point",
    spaceId: input.spaceId,
    location:
      input.location.z === undefined
        ? {
            x: input.location.x,
            y: input.location.y,
          }
        : {
            x: input.location.x,
            y: input.location.y,
            z: input.location.z,
          },
    rotation: input.rotation,
  };

  return {
    entity: buildSimpleGeometryPreviewEntity(entity, {
      summary: "Simple point created through the geometry command core.",
      properties: ceColorPreviewProperties(input.color),
    }),
    summary: `POINT created at ${input.location.x},${input.location.y}`,
  };
}

export function createCircleEntity(
  input: CreateCircleInput,
): GeometryMutationResult {
  const entity: SimpleCircleEntity = {
    id: input.nextId,
    type: "Circle",
    spaceId: input.spaceId,
    center:
      input.center.z === undefined
        ? {
            x: input.center.x,
            y: input.center.y,
          }
        : {
            x: input.center.x,
            y: input.center.y,
            z: input.center.z,
          },
    radius: input.radius,
  };

  return {
    entity: buildSimpleGeometryPreviewEntity(entity, {
      summary: "Simple circle created through the geometry command core.",
      properties: ceColorPreviewProperties(input.color),
    }),
    summary: `CIRCLE created at ${input.center.x},${input.center.y} with radius ${input.radius}`,
  };
}

export function createArcEntity(input: CreateArcInput): GeometryMutationResult {
  const entity: SimpleArcEntity = {
    id: input.nextId,
    type: "Arc",
    spaceId: input.spaceId,
    center:
      input.center.z === undefined
        ? {
            x: input.center.x,
            y: input.center.y,
          }
        : {
            x: input.center.x,
            y: input.center.y,
            z: input.center.z,
          },
    radius: input.radius,
    startAngle: input.startAngle,
    endAngle: input.endAngle,
  };

  return {
    entity: buildSimpleGeometryPreviewEntity(entity, {
      summary: "Simple arc created through the geometry command core.",
      properties: ceColorPreviewProperties(input.color),
    }),
    summary: `ARC created at ${input.center.x},${input.center.y} radius ${input.radius} angles ${input.startAngle}-${input.endAngle}`,
  };
}

/* ── ELLIPSE ────────────────────────────────────── */

/**
 * Build the EntityPreview side-channel record for the ELLIPSE created
 * by the geometry/create-ellipse reducer case. The preview drives the
 * properties panel, the audit feed and the renderer "Major axis X" /
 * "Major axis Y" / "Ratio" / "Start parameter" / "End parameter"
 * lookups (see renderSimpleEntities.tsx → renderEllipse).
 */
export function createEllipseEntity(
  input: CreateEllipseInput,
): GeometryMutationResult {
  const startParam = input.startParam ?? 0;
  const endParam = input.endParam ?? Math.PI * 2;
  const entity: SimpleEllipseEntity = {
    id: input.nextId,
    type: "Ellipse",
    spaceId: input.spaceId,
    center: { x: input.center.x, y: input.center.y },
    majorAxisEndpoint: {
      x: input.majorAxisEndpoint.x,
      y: input.majorAxisEndpoint.y,
    },
    ratio: input.ratio,
    startParam,
    endParam,
  };

  return {
    entity: buildEllipsePreviewEntity(entity, {
      summary: "Ellipse created through the geometry command core.",
      properties: ceColorPreviewProperties(input.color),
    }),
    summary: `ELLIPSE created at ${input.center.x},${input.center.y} ratio ${input.ratio}`,
  };
}

/* ── LWPOLYLINE ────────────────────────────────── */

type CreateLwpolylineInput = {
  nextId: string;
  spaceId: string;
  vertices: {
    x: number;
    y: number;
    bulge?: number;
    startWidth?: number;
    endWidth?: number;
  }[];
  closed: boolean;
  defaults?: Record<string, string>;
};

export function createLwpolylineEntity(
  input: CreateLwpolylineInput,
): GeometryMutationResult {
  const entity: SimpleLwpolylineEntity = {
    id: input.nextId,
    type: "LWPOLYLINE",
    spaceId: input.spaceId,
    vertices: input.vertices,
    closed: input.closed,
    constantWidth: 0,
    elevation: 0,
    thickness: 0,
  };

  return {
    entity: buildLwpolylinePreviewEntity(entity, {
      summary: "LWPOLYLINE created through the geometry command core.",
    }),
    summary: `LWPOLYLINE created with ${input.vertices.length} vertices, closed=${input.closed}`,
  };
}

/* ── TEXT ───────────────────────────────────────── */

type CreateTextInput = {
  nextId: string;
  spaceId: string;
  insertionPoint: SimpleGeometryPoint;
  height: number;
  contents: string;
  defaults?: { rotation?: number; styleName?: string };
};

export function createTextEntity(
  input: CreateTextInput,
): GeometryMutationResult {
  const entity: SimpleTextEntity = {
    id: input.nextId,
    type: "Text",
    spaceId: input.spaceId,
    insertionPoint: input.insertionPoint,
    height: input.height,
    rotation: input.defaults?.rotation ?? 0,
    contents: input.contents,
    styleName: input.defaults?.styleName ?? "Standard",
    widthFactor: 1,
    obliqueAngle: 0,
    hJustification: 0,
    vJustification: 0,
    thickness: 0,
  };

  return {
    entity: buildTextPreviewEntity(entity, {
      summary: "Text created through the geometry command core.",
    }),
    summary: `TEXT created at ${input.insertionPoint.x},${input.insertionPoint.y} height=${input.height}`,
  };
}

/* ── MTEXT ──────────────────────────────────────── */

type CreateMtextInput = {
  nextId: string;
  spaceId: string;
  insertionPoint: SimpleGeometryPoint;
  nominalHeight: number;
  referenceWidth: number;
  contents: string;
  defaults?: { styleName?: string };
};

export function createMtextEntity(
  input: CreateMtextInput,
): GeometryMutationResult {
  const entity: SimpleMtextEntity = {
    id: input.nextId,
    type: "MText",
    spaceId: input.spaceId,
    insertionPoint: input.insertionPoint,
    nominalHeight: input.nominalHeight,
    referenceWidth: input.referenceWidth,
    attachmentPoint: 1,
    drawingDirection: 1,
    contents: input.contents,
    styleName: input.defaults?.styleName ?? "Standard",
    rotation: 0,
    lineSpacingStyle: 1,
    lineSpacingFactor: 1,
    backgroundFill: false,
  };

  return {
    entity: buildMtextPreviewEntity(entity, {
      summary: "MText created through the geometry command core.",
    }),
    summary: `MTEXT created at ${input.insertionPoint.x},${input.insertionPoint.y} height=${input.nominalHeight} width=${input.referenceWidth}`,
  };
}

/* ── DIMENSION ──────────────────────────────────── */

type CreateDimensionInput = {
  nextId: string;
  spaceId: string;
  extLine1Point: SimpleGeometryPoint;
  extLine2Point: SimpleGeometryPoint;
  dimensionLinePoint: SimpleGeometryPoint;
  defaults?: { dimensionType?: number; dimensionText?: string };
};

export function createDimensionEntity(
  input: CreateDimensionInput,
): GeometryMutationResult {
  const entity: SimpleDimensionEntity = {
    id: input.nextId,
    type: "Dimension",
    spaceId: input.spaceId,
    styleName: "Standard",
    dimensionText: input.defaults?.dimensionText ?? "",
    textHeight: 2.5,
    extLine1Point: input.extLine1Point,
    extLine2Point: input.extLine2Point,
    dimensionLinePoint: input.dimensionLinePoint,
    measurement: 0,
    linearAngle: 0,
    dimensionType: input.defaults?.dimensionType ?? 0,
    obliqueAngle: 0,
    leaderLength: 0,
    anonymousBlockName: "",
    textRotation: 0,
  };

  return {
    entity: buildDimensionPreviewEntity(entity, {
      summary: "Dimension created through the geometry command core.",
    }),
    summary: `DIMENSION created ext1=${input.extLine1Point.x},${input.extLine1Point.y} ext2=${input.extLine2Point.x},${input.extLine2Point.y}`,
  };
}

/* ── MULTILEADER ────────────────────────────────── */

type CreateMultileaderInput = {
  nextId: string;
  spaceId: string;
  arrowPoint: SimpleGeometryPoint;
  landingPoint: SimpleGeometryPoint;
  textContents: string;
  defaults?: Record<string, string>;
};

export function createMultileaderEntity(
  input: CreateMultileaderInput,
): GeometryMutationResult {
  const entity: SimpleMultileaderEntity = {
    id: input.nextId,
    type: "Multileader",
    spaceId: input.spaceId,
    styleName: "Standard",
    textContents: input.textContents,
    textHeight: 2.5,
    arrowPoint: input.arrowPoint,
    landingPoint: input.landingPoint,
    arrowheadSize: 2.5,
  };

  return {
    entity: buildMultileaderPreviewEntity(entity, {
      summary: "Multileader created through the geometry command core.",
    }),
    summary: `MULTILEADER created arrow=${input.arrowPoint.x},${input.arrowPoint.y} landing=${input.landingPoint.x},${input.landingPoint.y}`,
  };
}

/* ── REVISION CLOUD ─────────────────────────────── */

type CreateRevisionCloudInput = {
  nextId: string;
  spaceId: string;
  // REVCLOUD-MODES — AutoCAD creation method. Omitted ⇒ "Rectangular"
  // (legacy two-corner flow). "Freehand" is accepted at the command level
  // and persisted as "Polygonal" (the audited RevcloudProps XData enum
  // carries no Freehand code — see contracts/src/editor.ts).
  creationMode?: "Rectangular" | "Polygonal" | "Freehand" | "Object";
  corner1?: SimpleGeometryPoint;
  corner2?: SimpleGeometryPoint;
  // Polygonal / Freehand picked path, or the Object-mode traced outline
  // (callers run `revisionCloudOutlineFromSource` first).
  vertices?: SimpleGeometryPoint[];
  // Object mode — id of the closed source entity the cloud replaces.
  sourceEntityId?: string;
  // AutoCAD "Reverse direction [Yes/No]" (Object mode) — flips the vertex
  // order so the arc bulges point to the other side of the outline.
  reverseDirection?: boolean;
  defaults?: { arcChordLength?: number; revisionCloudStyle?: string };
};

/** AABB corners of a vertex chain — keeps corner-based consumers (preview
 *  grid rows, legacy hit-test heuristics) working for non-rect clouds. */
function revcloudChainCorners(vertices: SimpleGeometryPoint[]): {
  corner1: SimpleGeometryPoint;
  corner2: SimpleGeometryPoint;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const v of vertices) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.x > maxX) maxX = v.x;
    if (v.y > maxY) maxY = v.y;
  }
  if (minX > maxX) {
    return { corner1: { x: 0, y: 0 }, corner2: { x: 0, y: 0 } };
  }
  return { corner1: { x: minX, y: minY }, corner2: { x: maxX, y: maxY } };
}

export function createRevisionCloudEntity(
  input: CreateRevisionCloudInput,
): GeometryMutationResult {
  const requestedMode = input.creationMode ?? "Rectangular";
  // Persisted mode — Freehand commits as a Polygonal vertex chain.
  const persistedMode =
    requestedMode === "Freehand" ? "Polygonal" : requestedMode;
  const style = input.defaults?.revisionCloudStyle ?? "Normal";
  const arcLength = input.defaults?.arcChordLength ?? 15;

  if (persistedMode === "Rectangular") {
    const corner1 = input.corner1 ?? { x: 0, y: 0 };
    const corner2 = input.corner2 ?? corner1;
    const entity: SimpleRevisionCloudEntity = {
      id: input.nextId,
      type: "Revision Cloud",
      spaceId: input.spaceId,
      revisionCloudStyle: style,
      approximateArcLength: arcLength,
      creationMode: "Rectangular",
      corner1,
      corner2,
    };
    return {
      entity: buildRevisionCloudPreviewEntity(entity, {
        summary: "Revision Cloud created through the geometry command core.",
      }),
      summary: `REVCLOUD created from ${corner1.x},${corner1.y} to ${corner2.x},${corner2.y}`,
    };
  }

  const sourceVertices = input.vertices ?? [];
  const chain = input.reverseDirection
    ? [...sourceVertices].reverse()
    : sourceVertices;
  const { corner1, corner2 } = revcloudChainCorners(chain);
  const entity: SimpleRevisionCloudEntity = {
    id: input.nextId,
    type: "Revision Cloud",
    spaceId: input.spaceId,
    revisionCloudStyle: style,
    approximateArcLength: arcLength,
    creationMode: persistedMode,
    vertices: chain,
    ...(input.sourceEntityId !== undefined
      ? { sourceEntityId: input.sourceEntityId }
      : {}),
    corner1,
    corner2,
  };
  return {
    entity: buildRevisionCloudPreviewEntity(entity, {
      summary: "Revision Cloud created through the geometry command core.",
    }),
    summary:
      persistedMode === "Object"
        ? `REVCLOUD created from object ${input.sourceEntityId ?? ""} (${chain.length} vertices)`
        : `REVCLOUD created along ${chain.length} ${requestedMode === "Freehand" ? "freehand" : "polygon"} vertices`,
  };
}

/* ── HATCH ──────────────────────────────────────── */

type CreateHatchInput = {
  nextId: string;
  spaceId: string;
  boundaryCorner1: SimpleGeometryPoint;
  boundaryCorner2: SimpleGeometryPoint;
  patternName: string;
  defaults?: { patternScale?: number; patternAngle?: number };
};

export function createHatchEntity(
  input: CreateHatchInput,
): GeometryMutationResult {
  const entity: SimpleHatchEntity = {
    id: input.nextId,
    type: "Hatch",
    spaceId: input.spaceId,
    patternName: input.patternName,
    patternScale: input.defaults?.patternScale ?? 1,
    patternAngle: input.defaults?.patternAngle ?? 0,
    associative: true,
    hatchStyle: 0,
    patternType: 1,
  };

  return {
    entity: buildHatchPreviewEntity(entity, {
      summary: "Hatch created through the geometry command core.",
    }),
    summary: `HATCH created pattern=${input.patternName} from ${input.boundaryCorner1.x},${input.boundaryCorner1.y} to ${input.boundaryCorner2.x},${input.boundaryCorner2.y}`,
  };
}

/* ── BLOCK INSERT ───────────────────────────────── */

type CreateBlockInsertInput = {
  nextId: string;
  spaceId: string;
  blockName: string;
  insertionPoint: SimpleGeometryPoint;
  defaults?: {
    scaleX?: number;
    scaleY?: number;
    scaleZ?: number;
    rotation?: number;
  };
};

export function createBlockInsertEntity(
  input: CreateBlockInsertInput,
): GeometryMutationResult {
  const entity: SimpleBlockInsertEntity = {
    id: input.nextId,
    type: "Block",
    spaceId: input.spaceId,
    blockName: input.blockName,
    insertionPoint: input.insertionPoint,
    scaleX: input.defaults?.scaleX ?? 1,
    scaleY: input.defaults?.scaleY ?? 1,
    scaleZ: input.defaults?.scaleZ ?? 1,
    rotation: input.defaults?.rotation ?? 0,
    columnCount: 1,
    rowCount: 1,
    columnSpacing: 0,
    rowSpacing: 0,
  };

  return {
    entity: buildBlockInsertPreviewEntity(entity, {
      summary: "Block INSERT created through the geometry command core.",
    }),
    summary: `BLOCK INSERT created name=${input.blockName} at ${input.insertionPoint.x},${input.insertionPoint.y}`,
  };
}

/* ── VIEWPORT ───────────────────────────────────── */

export type CreateViewportInput = {
  nextId: string;
  spaceId: string;
  corner1: SimpleGeometryPoint;
  corner2: SimpleGeometryPoint;
  defaults?: Record<string, string>;
  /**
   * Model-space extent the viewport should auto-fit to. When provided,
   * `viewCenterX`/`viewCenterY` default to the extent centre and
   * `viewHeight` is scaled to contain the full extent (with a 10 %
   * margin) while preserving the paper-viewport aspect ratio. Matches
   * AutoCAD's default MVIEW "FIT" behaviour. When omitted OR when
   * `minX===maxX && minY===maxY` (zero-area extent), the legacy
   * `viewHeight=100`, `viewCenter=(0,0)` defaults are used.
   */
  modelExtent?: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  };
};

export function createViewportEntity(
  input: CreateViewportInput,
): GeometryMutationResult {
  const centerX = (input.corner1.x + input.corner2.x) / 2;
  const centerY = (input.corner1.y + input.corner2.y) / 2;
  const width = Math.abs(input.corner2.x - input.corner1.x);
  const height = Math.abs(input.corner2.y - input.corner1.y);

  const extent = input.modelExtent;
  const modelW = extent ? extent.maxX - extent.minX : 0;
  const modelH = extent ? extent.maxY - extent.minY : 0;
  // Paper-viewport aspect ratio. Height can be 0 if corner1 === corner2
  // (degenerate pick); fall back to 1 to avoid NaN.
  const aspect = height > 0 ? width / height : 1;

  let viewHeight: number;
  let viewCenterX: number;
  let viewCenterY: number;
  if (extent && modelW > 0 && modelH > 0) {
    // Fit model into the viewport rectangle, preserving aspect ratio.
    // viewHeight is the model-height visible through the viewport; when
    // the model is wider than the viewport can show at that height, we
    // enlarge viewHeight so the model's width fits too (10 % margin on
    // each axis, matching AutoCAD's default MVIEW-FIT padding).
    const fitByH = modelH * 1.1;
    const fitByW = (modelW * 1.1) / aspect;
    viewHeight = Math.max(fitByH, fitByW);
    viewCenterX = (extent.minX + extent.maxX) / 2;
    viewCenterY = (extent.minY + extent.maxY) / 2;
  } else {
    viewHeight = 100;
    viewCenterX = 0;
    viewCenterY = 0;
  }

  const entity: SimpleViewportEntity = {
    id: input.nextId,
    type: "Viewport",
    spaceId: input.spaceId,
    center: { x: centerX, y: centerY },
    width,
    height,
    displayLocked: false,
    scale: "1:1",
    shadePlot: "As Displayed",
    viewHeight,
    viewCenterX,
    viewCenterY,
    // AutoCAD MVIEW default: a freshly created viewport has no view twist.
    viewTwistAngle: 0,
    layer: "0",
  };

  return {
    entity: buildViewportPreviewEntity(entity, {
      summary: "Viewport created through the geometry command core.",
    }),
    summary: `VIEWPORT created center=${centerX},${centerY} size=${width}x${height}`,
  };
}

export function appendCreatedEntity(
  entities: EntityPreview[],
  entity: EntityPreview,
): EntityPreview[] {
  return [...entities, entity];
}

/* ── Block mutations (BLOCK / INSERT / EXPLODE) ─────── */
// Implementation lives in a dedicated module to keep this file under
// the 500-line target for new material (CLAUDE.md size guidelines).
// All public names are re-exported so callers continue importing from
// `@dxf-ai-editor/command-engine` without noticing the internal split.
export {
  validateBlockName,
  detectCyclicBlockReference,
  createBlockDefinitionMutation,
  createBlockInsertEntityFull,
  explodeInsertMutation,
  annoupdateMutation,
  objectScaleAddMutation,
  objectScaleRemoveMutation,
  type BlockNameValidationError,
  type BlockPostAction,
  type CreateBlockDefinitionInput,
  type CreateBlockDefinitionResult,
  type CreateBlockInsertFullInput,
  type CreateBlockInsertFullResult,
  type ExplodeInsertInput,
  type ExplodeInsertResult,
  type ResolveNestedMode,
  type AnnoupdateInput,
  type AnnoupdateResult,
  type ObjectScaleAddInput,
  type ObjectScaleRemoveInput,
  type ObjectScaleResult,
} from "./blockMutations";

/* ── Block file operations (WBLOCK / PURGE / RENAME) ─────────── */
// Phase 2 tasks 2.6 + 2.7 + 2.8. Full-document-scope mutations live in a
// dedicated module so blockMutations.ts stays below the CLAUDE.md 2000-
// line hard limit while keeping a single import path for callers.
export {
  wblockExportMutation,
  purgeBlocksMutation,
  applyPurgeBlocks,
  renameBlockMutation,
  validateRenameBlock,
  type WblockSource,
  type WblockExportInput,
  type WblockExportResult,
  type PurgeBlocksInput,
  type PurgeCandidate,
  type PurgeBlocksAnalysisResult,
  type ApplyPurgeBlocksResult,
  type RenameBlockInput,
  type RenameBlockResult,
  type RenameBlockValidationError,
} from "./blockFileOperations";

/* ── Attribute mutations (ATTDEF / DDATTE / ATTEDIT / EATTEDIT) ─────── */
// Phase 2.1 + 2.2. Attribute authoring and editing live in a dedicated
// module (attributeMutations.ts) so blockMutations.ts stays below the
// 2000-line hard limit. Public names are re-exported here so callers
// keep their single import path.
export {
  createAttdefMutation,
  validateAttdefInput,
  updateAttributeValueMutation,
  updateAttributeValuesMutation,
  type AttdefFlagsInput,
  type AttdefValidationError,
  type AttributeOverridesInput,
  type CreateAttdefInput,
  type CreateAttdefResult,
  type UpdateAttributeValueInput,
  type UpdateAttributeValueResult,
  type UpdateAttributeValuesInput,
  type UpdateAttributeValuesResult,
} from "./attributeMutations";

/* ── BATTMAN / ATTSYNC mutations (Phase 2.4 + 2.5) ──────────────── */
// Block Attribute Manager operations (remove/update/rename-tag/reorder
// ATTDEF in a block) plus ATTSYNC. Separate modules keep
// attributeMutations.ts below the size cap and group the Phase 2.4/2.5
// surface coherently.
export {
  removeAttdefMutation,
  updateAttdefMutation,
  renameAttdefTagMutation,
  reorderAttdefsMutation,
  type RemoveAttdefInput,
  type RemoveAttdefResult,
  type UpdateAttdefInput,
  type UpdateAttdefResult,
  type RenameAttdefTagInput,
  type RenameAttdefTagResult,
  type ReorderAttdefsInput,
  type ReorderAttdefsResult,
} from "./battmanMutations";
export {
  attsyncMutation,
  type AttsyncInput,
  type AttsyncResult,
  type AttsyncStats,
} from "./attsyncMutation";

/* ── XREF suite (Phase FIN.3 — XATTACH / XBIND / XUNLOAD / XRELOAD /
 *    XDETACH / XCLIP). External-reference workflow. Browser file API
 *    handling lives at the App.tsx layer; this module only operates on
 *    pre-parsed entities (XATTACH) and applies status / boundary
 *    updates. Re-exported here so consumers keep a single import path.
 */
export {
  isXrefBlock,
  xattachMutation,
  xbindMutation,
  xunloadMutation,
  xreloadMutation,
  xdetachMutation,
  xclipMutation,
  XREF_FLAG_BIT,
  XREF_OVERLAY_FLAG_BIT,
  XREF_FLAGS_MASK,
  type XattachInput,
  type XattachResult,
  type XbindInput,
  type XbindResult,
  type XunloadInput,
  type XreloadInput,
  type XdetachInput,
  type XclipInput,
} from "./xrefMutations";

/* ── Dynamic block authoring mutations (DYN-1.7 + DYN-1.8) ────────── */
// Parameter / action CRUD and visibility-state management on the
// BlockDefinition.dynamicMetadata subtree. Lives in its own module so
// the authoring surface stays cohesive and the re-export keeps the
// one-import convention.
export {
  addDynamicParameterMutation,
  updateDynamicParameterMutation,
  removeDynamicParameterMutation,
  addDynamicActionMutation,
  updateDynamicActionMutation,
  removeDynamicActionMutation,
  addVisibilityStateMutation,
  renameVisibilityStateMutation,
  deleteVisibilityStateMutation,
  reorderVisibilityStatesMutation,
  setDefaultVisibilityStateMutation,
  toggleVisibilityStateEntityMutation,
  // Phase K.4 — Visibility States Manager additions
  setCurrentVisibilityStateMutation,
  bvhideMutation,
  bvshowMutation,
  bvshowAllMutation,
  // DYN-3 Batch C: chain group mutations
  createChainGroupMutation,
  addActionToChainGroupMutation,
  removeActionFromChainMutation,
  reorderChainGroupMutation,
  type MutationResult,
  type AddDynamicParameterInput,
  type UpdateDynamicParameterInput,
  type RemoveDynamicParameterInput,
  type AddDynamicActionInput,
  type UpdateDynamicActionInput,
  type RemoveDynamicActionInput,
  type AddVisibilityStateInput,
  type RenameVisibilityStateInput,
  type DeleteVisibilityStateInput,
  type ReorderVisibilityStatesInput,
  type SetDefaultVisibilityStateInput,
  type ToggleVisibilityStateEntityInput,
  // Phase K.4 inputs
  type SetCurrentVisibilityStateInput,
  type BvhideInput,
  type BvshowInput,
  type BvshowAllInput,
  type CreateChainGroupInput,
  type AddActionToChainGroupInput,
  type RemoveActionFromChainInput,
  type ReorderChainGroupInput,
} from "./dynamicAuthoringMutations";

// DYN-3 Batch D — geometric constraint mutations
export {
  addGeometricConstraintMutation,
  updateGeometricConstraintMutation,
  removeGeometricConstraintMutation,
  type GeometricConstraintMutationResult,
  type AddGeometricConstraintInput,
  type UpdateGeometricConstraintInput,
  type RemoveGeometricConstraintInput,
} from "./geometricConstraintMutations";

/* ── Lookup table + Block properties table mutations (DYN-2 B.2) ──── */
// Manage the LookupTable[] array and BlockPropertiesTable payload that
// hang off BlockDefinition.dynamicMetadata. Same re-export pattern as
// above so every call-site resolves through @dxf-ai-editor/command-engine.
export {
  createLookupTableMutation,
  updateLookupTableMutation,
  deleteLookupTableMutation,
  setBlockPropertiesTableMutation,
  addBlockPropertyMutation,
  updateBlockPropertyMutation,
  removeBlockPropertyMutation,
  reorderBlockPropertiesMutation,
  type CreateLookupTableInput,
  type UpdateLookupTableInput,
  type DeleteLookupTableInput,
  type SetBlockPropertiesTableInput,
  type AddBlockPropertyInput,
  type UpdateBlockPropertyInput,
  type RemoveBlockPropertyInput,
  type ReorderBlockPropertiesInput,
} from "./dynamicLookupMutations";

/* ── Phase N.5 — Block Properties Manager (BPM) mutations ─────────── */
// Drawing-wide parameter / action editing — operates on any block by name
// without requiring an active BEDIT session. Used by the
// BLOCKPROPERTIESMANAGER (BPM) dialog. See blockPropertiesManagerMutations.ts
// for full docs on each mutation.
export {
  renameBlockParameterMutation,
  updateBlockParameterDescriptionMutation,
  toggleBlockParameterShowInPaletteMutation,
  reorderBlockParametersMutation,
  renameBlockActionMutation,
  updateBlockActionDescriptionMutation,
  reorderBlockActionsMutation,
  type RenameBlockParameterInput,
  type UpdateBlockParameterDescriptionInput,
  type ToggleBlockParameterShowInPaletteInput,
  type ReorderBlockParametersInput,
  type RenameBlockActionInput,
  type UpdateBlockActionDescriptionInput,
  type ReorderBlockActionsInput,
} from "./blockPropertiesManagerMutations";

/* ── computeInverse ─────────────────────────────────────────────
 *
 * Phase 4.1.c.2 follow-up — Full-fidelity inverse contracts for geometry
 * create/delete and shell/delete-entities.
 *
 * Supported pairs:
 *   geometry/create-* (every single-append create: line / point / circle /
 *   arc / ellipse / spline / lwpolyline / donut / text / mtext / dimension /
 *   multileader / revcloud / wipeout / hatch / viewport)
 *                              ↔  geometry/delete-entity
 *     (FF-4 + HIST-{MTEXT,HATCH,MLEADER,REVCLOUD}-INVERSE — AutoCAD U undoes
 *      ANY create; see SINGLE_CREATE_ENTITY_TYPE for the type-string map.)
 *
 *   geometry/create-array{rect,polar,path} / geometry/arrayedit
 *                              ↔  history/replay-inverses batch of
 *                                 shell/delete-entities + shell/restore-entities
 *     (FF-1/FF-2 — the forward tags the sources AND mints copy clones, so the
 *      inverse deletes the whole post-mutation member set and restores the
 *      pre-mutation snapshots verbatim.)
 *
 *   geometry/delete-entity     ↔  geometry/restore-entity  (P1-3/P1-5: full snapshot, preserves id)
 *   shell/delete-entities      ↔  shell/restore-entities   (P1-4: full snapshot array)
 *
 * Codex P1-3: Entity id is preserved through undo/redo — the restore-entity
 *   command carries the full DrawingEntity snapshot including the original id.
 * Codex P1-5: Full entity payload — no field stripping. Layer, color,
 *   lineweight, transparency, custom properties, etc. are all preserved via
 *   `JSON.parse(JSON.stringify(entity))` deep clone.
 * Codex P1-4: shell/delete-entities now handled — emits shell/restore-entities.
 */

/**
 * The viewport-clip fields a cleared-viewport `updated` diff must expose so
 * `computeInverse` can detect a dangling clip that was cleared by the delete
 * (PR #324 / #318 R2) and rebuild it on undo. A structural subset of
 * `ViewportEntity` — kept minimal here rather than importing the full type so
 * the `next: { id }`-only create-path mocks keep compiling (see Step-1 note).
 */
interface ViewportClipFields {
  type?: string;
  clippingBoundaryHandle?: string;
  statusBitFlags?: number;
  extraGroups?: ReadonlyArray<{ code: number; value: string }>;
}

/** Minimal forward-mutations shape needed for computeInverse (mirror/offset/
 *  copy/create/array/delete). `removed`/`updated` are the extra dispatch-diff
 *  slices the ARRAY + DELETE inverses need: a 1×1 ARRAYRECT mints no copies but
 *  still tags the sources (added=[] / updated=[sources]), an ARRAYEDIT that
 *  trips the MAXARRAY guard mid-flight deletes the old copies without minting
 *  new ones (added=[] / removed=[old copies]), and a DELETE of a viewport-clip
 *  boundary clears the surviving viewport's clip in place (updated=[cleared VP],
 *  prev = the clip snapshot, next = the cleared VP).
 *
 *  Step-1 widening (PR #324 P1): `updated` element carries the FULL `spaceId` +
 *  `prev`/`next` the runtime `EntityMutation.updated` diff threads (see
 *  apps/web/.../types.ts `EntityMutation`). The DELETE inverse reads
 *  `prev`/`next` to find a cleared viewport clip; the ARRAY inverses read only
 *  `next.id`. `prev` is OPTIONAL + typed (not `DrawingEntity`) and `next` keeps
 *  the `{ id: string }` floor so the existing create/array test mocks that pass
 *  `{ next: { id: "line-1" } }` (no `prev`, no full entity) still typecheck —
 *  `DrawingEntity` would reject those mocks (missing required fields). The
 *  viewport-clip fields ride along as the `ViewportClipFields` intersection so
 *  the delete branch reads them type-safely WITHOUT an `any` cast. All optional
 *  so legacy callers that thread only `added` keep compiling. */
interface ForwardMutations {
  added: ReadonlyArray<{ entity: { id: string } }>;
  removed?: ReadonlyArray<{ entityId: string }>;
  updated?: ReadonlyArray<{
    spaceId?: string;
    prev?: ({ id: string } & ViewportClipFields) | undefined;
    next: { id: string } & ViewportClipFields;
  }>;
}

type GeometryDeleteEntityCommand = {
  type: "geometry/delete-entity";
  source: CommandSource;
  entityId: string;
};

type ShellDeleteEntitiesCommand = {
  type: "shell/delete-entities";
  source: CommandSource;
  entityIds: string[];
};

function findLastEntityOfTypeInSpace(
  doc: DrawingDocument,
  spaceId: string,
  entityType: string,
): DrawingEntity | undefined {
  const space = doc.spaces.find((s) => s.id === spaceId);
  if (!space) return undefined;
  const matches = space.entities.filter((e) => e.type === entityType);
  return matches[matches.length - 1];
}

function findEntityById(
  doc: DrawingDocument,
  entityId: string,
): DrawingEntity | undefined {
  for (const space of doc.spaces) {
    const found = space.entities.find((e) => e.id === entityId);
    if (found) return found;
  }
  return undefined;
}

/**
 * Resolve MULTIPLE entity ids in a SINGLE O(total) pass over `doc.spaces`
 * (each space's entities scanned once), instead of one `findEntityById` call
 * per id — which itself re-scans every space/entity from scratch, making a
 * per-id loop O(selected × total) (GitHub Codex review, PR #558 P2 — the
 * `geometry/rotate-entities`/`geometry/mirror-entities` `computeInverse`
 * snapshot loops below). Early-exits once every requested id has been found
 * (the common case: a small selection against a large document); a dangling
 * id that resolves to nothing is simply absent from the returned map,
 * matching `findEntityById`'s `undefined` return.
 */
function findEntitiesByIds(
  doc: DrawingDocument,
  entityIds: readonly string[],
): Map<string, DrawingEntity> {
  const wanted = new Set(entityIds);
  const result = new Map<string, DrawingEntity>();
  // targeted-cad-mutation-testing EQUIVALENT MUTANT (GitHub Codex review PR
  // #558 P2 — verified survivor): ConditionalExpression forcing `wanted.size
  // === 0` → `false` (this guard never fires). Why equivalent: this is a
  // pure PERFORMANCE short-circuit for the degenerate empty-request case —
  // with `wanted` empty, `wanted.has(entity.id)` below is `false` for every
  // entity the main loop visits (an empty `Set` matches nothing), so the
  // loop falls through to `return result` with the SAME empty `Map`
  // regardless of whether this early return fired. The only difference is
  // whether the whole document gets needlessly scanned first — not an
  // observable output difference for any caller.
  if (wanted.size === 0) return result;
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      // targeted-cad-mutation-testing EQUIVALENT MUTANT (GitHub Codex review
      // PR #558 P2 — verified survivor): ConditionalExpression forcing
      // `wanted.has(entity.id)` → `true` (every entity gets inserted, not
      // just the requested ones). Why equivalent UNDER THE CURRENT, SOLE
      // consumption pattern: both call sites (`geometry/rotate-entities`'s
      // and `geometry/mirror-entities`'s `computeInverse` snapshot loops,
      // below) read this map ONLY via `entitiesById.get(id)` where `id` is
      // drawn from the EXACT SAME `cmd.entityIds` array used to build
      // `wanted` here — never `.values()`/`.keys()`/`.size`/iteration over
      // the whole map. `DrawingDocument` entity ids are unique within a
      // document (schema/mint-allocator invariant), so an ungated
      // `result.set(entity.id, entity)` for a NOT-wanted entity adds a
      // harmless extra key the two current call sites never query — it
      // cannot shadow or corrupt a wanted entry (different key). A FUTURE
      // caller that iterates the map's full contents (rather than `.get`-ing
      // only requested ids) would make this mutant observable again and
      // must re-verify this equivalence before relying on it.
      if (wanted.has(entity.id)) {
        result.set(entity.id, entity);
        // targeted-cad-mutation-testing EQUIVALENT MUTANT (GitHub Codex
        // review PR #558 P2 — verified survivor, 3 mutator variants:
        // ConditionalExpression→`true`, ConditionalExpression→`false`,
        // EqualityOperator `===`→`!==`): this early-exit is the SAME
        // pure-performance optimisation as the `wanted.size === 0` guard
        // above — once every requested id has already been found, the
        // remaining loop iterations can only ever re-visit ALREADY-`.set()`
        // entities with a different id (unique-id invariant, same argument
        // as above) or entities not in `wanted` (which `wanted.has()` above
        // already excludes) — so continuing the scan instead of returning
        // early changes NOTHING about the FINAL map's contents, only how
        // much of the document gets needlessly walked afterward.
        if (result.size === wanted.size) return result;
      }
    }
  }
  return result;
}

function findEntitySpaceId(doc: DrawingDocument, entityId: string): string | undefined {
  for (const space of doc.spaces) {
    if (space.entities.some((e) => e.id === entityId)) return space.id;
  }
  return undefined;
}

/** Deep-clone an entity snapshot preserving all fields (P1-5). */
function cloneEntity(entity: DrawingEntity): DrawingEntity {
  return JSON.parse(JSON.stringify(entity)) as DrawingEntity;
}

/** Locate a Viewport entity by id across all spaces (for inverse computation).
 *  CASE-INSENSITIVE on the DXF handle (mirrors the forward VPCLIP producer's
 *  `findViewportById`) so a `VPCLIP 2a …` whose viewport id is stored as `2A`
 *  still resolves an inverse — otherwise the forward would mutate but Undo would
 *  record nothing (@codex #496 P2). */
function findViewportInDoc(doc: DrawingDocument, viewportId: string): ViewportEntity | undefined {
  const target = viewportId.trim().toLowerCase();
  for (const space of doc.spaces) {
    const vp = space.entities.find(
      (e) => e.type === "Viewport" && e.id.trim().toLowerCase() === target,
    );
    if (vp) return vp as unknown as ViewportEntity;
  }
  return undefined;
}

/**
 * Build the FULL-SNAPSHOT undo inverse for a VPCLIP authoring / mutation command
 * (`viewport-clip-set-polygon`, rectangular `viewport-clip-set`,
 * `viewport-clip-remove`). Each of those forwards can supersede the viewport's
 * WHOLE clip state — invalidate a prior polygon / imported SPATIAL_FILTER clip,
 * reshape the rectangle + rescale viewHeight + drop childEntityIds, or clear the
 * boundary outright — so a field-level inverse (bounds only) would lose the
 * cleared non-rectangular clip on undo (@codex #496 P2). The faithful inverse is
 * a verbatim re-set of the PRE-mutation viewport via the general in-place
 * `geometry/restore-viewport-clip` snapshot-replace (preserve-source, no field
 * inferred — it swaps the entity matched by id for its full pre-edit snapshot).
 *
 * Returns null for a missing viewport or the paper-space control viewport
 * (id === 1) — both are no-ops in the sibling producers, so they record no undo.
 * `doc` MUST be the PRE-mutation document (the dispatcher passes preMutationDoc
 * for these commands).
 */
function buildViewportClipSnapshotInverse(
  doc: DrawingDocument,
  viewportId: string,
  source: CommandSource,
): EditorCommand | null {
  const vp = findViewportInDoc(doc, viewportId);
  if (!vp || vp.viewportId === 1) return null;
  // Use the CANONICAL entity id (`vp.id`) — `viewportId` may differ in hex case
  // (findViewportInDoc matched case-insensitively), and `findEntitySpaceId` is an
  // exact-match lookup (@codex #496 P2).
  const spaceId = findEntitySpaceId(doc, vp.id);
  if (!spaceId) return null;
  // @codex #496 P2 — the three VPCLIP forwards (set / set-polygon / remove) ALSO
  // strip the imported SPATIAL_FILTER OBJECTS chain
  // (ext-dict → ACAD_FILTER → SPATIAL_FILTER) out of the document-scope
  // `rawObjectGroups` via `detachInvalidatedViewportClipObjects`. The per-viewport
  // entity snapshot above restores the 340 handle + ext-dict handle but CANNOT
  // reach that stripped OBJECTS stream, so a strip → undo → save would re-emit a
  // viewport whose restored 340/ext-dict points at a now-missing SPATIAL_FILTER
  // (the imported clip is lost / dangling on reload). When THIS viewport actually
  // carries a strippable chain — detected by running the SAME idempotent strip on
  // the PRE-mutation doc/vp and seeing it change the document (`invalidateViewportClip`
  // preserves `extensionDictionaryHandle`, the strip key, so the pre-mutation vp
  // predicts the forward's `[updated]` strip exactly) — snapshot the PRE-mutation
  // `rawObjectGroups` VERBATIM so undo re-attaches the removed records
  // (preserve-source, no field inferred). Omitted when nothing would be stripped,
  // keeping the common rectangular-reshape undo lean.
  const restoreRawObjectGroups =
    doc.rawObjectGroups &&
    detachInvalidatedViewportClipObjects(doc, [
      vp as unknown as ViewportEntity,
    ]) !== doc
      ? doc.rawObjectGroups.map((g) => ({ code: g.code, value: g.value }))
      : undefined;
  const inverse: Record<string, unknown> = {
    type: "geometry/restore-viewport-clip",
    source,
    viewports: [
      { spaceId, viewport: cloneEntity(vp as unknown as DrawingEntity) },
    ],
  };
  if (restoreRawObjectGroups) {
    inverse.restoreRawObjectGroups = restoreRawObjectGroups;
  }
  return inverse as unknown as EditorCommand;
}

/** Entity `type` string minted by each single-append geometry/create-*
 *  command (the reducer appends exactly one entity per dispatch). DONUT is
 *  encoded as a closed LWPOLYLINE with two bulge=1 vertices — AutoCAD models
 *  DONUT exactly this way (see geometryReducer create-donut). */
const SINGLE_CREATE_ENTITY_TYPE = {
  "geometry/create-line": "Line",
  "geometry/create-point": "Point",
  "geometry/create-circle": "Circle",
  "geometry/create-arc": "Arc",
  "geometry/create-ellipse": "Ellipse",
  "geometry/create-spline": "Spline",
  "geometry/create-lwpolyline": "LWPOLYLINE",
  "geometry/create-donut": "LWPOLYLINE",
  "geometry/create-text": "Text",
  "geometry/create-mtext": "MText",
  "geometry/create-dimension": "Dimension",
  "geometry/create-multileader": "Multileader",
  "geometry/create-revcloud": "Revision Cloud",
  "geometry/create-wipeout": "Wipeout",
  "geometry/create-hatch": "Hatch",
  "geometry/create-viewport": "Viewport",
} as const satisfies Record<string, string>;

/**
 * Inverse for the single-append `geometry/create-*` commands: delete the
 * entity the forward just minted. Prefers the forward-mutations payload — it
 * carries the EXACT minted id and is empty when the forward no-opped (e.g.
 * the create-hatch defensive no-boundary path, or MVIEW into a non-layout
 * space), where last-of-type matching would wrongly target a PRE-EXISTING
 * entity of the same type. Falls back to last-of-type-in-space (the reducer
 * appends, so the just-created entity is last) for callers that do not
 * thread mutations.
 */
function inverseForSingleEntityCreate(
  doc: DrawingDocument,
  source: CommandSource,
  spaceId: string,
  entityType: string,
  mutations?: ForwardMutations,
): EditorCommand | null {
  if (mutations) {
    if (mutations.added.length === 0) return null;
    if (mutations.added.length > 1) {
      // Defensive — single-append commands mint exactly one entity, but if a
      // dispatch ever fans out, every minted id must be removed on undo.
      return {
        type: "shell/delete-entities",
        source,
        entityIds: mutations.added.map((a) => a.entity.id),
      } as unknown as EditorCommand;
    }
    return {
      type: "geometry/delete-entity",
      source,
      entityId: mutations.added[0]!.entity.id,
    } as unknown as EditorCommand;
  }
  const entity = findLastEntityOfTypeInSpace(doc, spaceId, entityType);
  if (!entity) return null;
  return {
    type: "geometry/delete-entity",
    source,
    entityId: entity.id,
  } as unknown as EditorCommand;
}

/**
 * Compose the delete+restore inverse used by the ARRAY family: remove the
 * post-mutation member set, then re-append the pre-mutation snapshots. Same
 * history-internal batch shape as the create-block-definition delete/convert
 * inverse — replay-inverses dispatches each member in array order with
 * __bypassHistory (see editorStore history/replay-inverses). Delete runs
 * FIRST so restoring a snapshot never collides with a surviving id.
 * NOTE: shell/restore-entities re-APPENDS, so undo preserves the entity SET
 * verbatim but may move the restored members to the end of their space's
 * draw order — the same accepted trade-off as the create-block-definition
 * source restore.
 */
function buildArrayDeleteRestoreInverse(
  deleteIds: string[],
  restoreEntities: DrawingEntity[],
  spaceId: string,
): EditorCommand {
  return {
    type: "history/replay-inverses",
    source: "system",
    batch: [
      {
        type: "shell/delete-entities",
        source: "system",
        entityIds: deleteIds,
      },
      {
        type: "shell/restore-entities",
        source: "system",
        entities: restoreEntities,
        spaceId,
      },
    ],
  } as unknown as EditorCommand;
}

/**
 * D4 — snapshot-based inverse for the 10 modify commands whose forward
 * reducer only ever reports its change through the generic `ForwardMutations`
 * diff (TRIM / EXTEND / FILLET / CHAMFER / BREAK / JOIN / EXPLODE-ENTITIES /
 * EXPLODE-INSERT / LENGTHEN / STRETCH — `applyEditTrimActionWithMutations` /
 * `explodeBlockInsertInDocument`'s `wrapHelperResult` diff on the app side).
 * Every one of these either mutates an entity IN PLACE (id preserved —
 * reported as `updated`), mints brand-new entities (`added`), or drops a
 * source entity entirely (`removed`, id only — no snapshot, the SAME
 * contract `geometry/delete-entity`'s P1-3 inverse relies on).
 *
 * Mirrors `buildArrayDeleteRestoreInverse` (the ARRAY family's inverse
 * shape) exactly: delete every id the forward touched-in-place or minted,
 * then re-append the PRE-mutation snapshot of every id the forward changed
 * or removed. Every snapshot is read from `preMutationDoc` (NOT from
 * `mutations.updated[].prev`, which the package-level `ForwardMutations`
 * type narrows to `{id} & ViewportClipFields` for mock-compatibility, not a
 * full `DrawingEntity`) — `preMutationDoc` is the ONLY place a `removed`
 * entry's full payload survives (`mutations.removed` carries just the id),
 * so reading `updated` snapshots the same way keeps both codepaths uniform
 * and gives byte-identical results (same pre-dispatch document either way).
 *
 * Handle-reuse safe by construction: if a forward diff ever reused a
 * removed source's id for one of the newly-minted entities (e.g. an
 * EXPLODE child landing on the exploded INSERT's own id), the structural
 * diff `applyEditTrimActionWithMutations` / `wrapHelperResult` produce
 * classifies that id as `updated` (same id, different shape) rather than a
 * separate remove+add — so this helper naturally deletes the reused id and
 * restores the ORIGINAL pre-mutation entity at that id, then separately
 * deletes every genuinely-new sibling id. No special-casing needed.
 *
 * `null` when the forward mutation was empty (a no-op dispatch — e.g. TRIM
 * with no intersecting cutting edge, or FILLET/CHAMFER on non-Line targets)
 * so an idle command pushes no history entry.
 */
/**
 * D4 follow-up (@codex P2, PR #468 review) — structural deep-equal for a
 * `DrawingEntity` snapshot pair, so an `updated` diff record whose `next` is
 * CONTENT-IDENTICAL to the pre-mutation snapshot is recognised as a no-op
 * (not a real shape change) before it is added to the delete/restore inverse
 * below. Two known producers of such a record: (1) a no-op STRETCH — the
 * per-type branches above always spread the matched entity into a FRESH
 * object (`{...entity, vertices: entity.vertices.map(...)}`) even when the
 * stretch WINDOW covers none of its points, so `nextEntity !== entity`
 * (reference inequality) even though every coordinate is numerically
 * unchanged; (2) an EXPLODE-for-Block-insert forward, which rebuilds the
 * WHOLE document via `assembleDrawingDocument`
 * (`explodeBlockInsertInDocument` / `wrapHelperResult`'s
 * `diffDocumentMutations`), so an UNRELATED survivor can come back through
 * the diff as a freshly re-parsed (but semantically unchanged) object. Undo
 * must never delete+re-append geometry the forward never actually touched —
 * needless whole-drawing churn AND a silent draw-order shuffle for entities
 * the user did not edit.
 *
 * Mirrors `entitiesShapeEqual` (apps/web geometryMutationHelpers.ts — the
 * SAME survivor-vs-Zod-reparse problem `diffSpaceEntities` already guards
 * against for its OWN diff): id/type fast-mismatch, then a `JSON.stringify`
 * payload compare. Reliable here because both operands are always read from
 * a `DrawingDocument` that has already round-tripped through the SAME entity
 * Zod schema (`assembleDrawingDocument*`), so a content-identical entity is
 * ALSO key-order-identical (Zod always emits an object schema's keys in the
 * schema's OWN declared order, never the input's insertion order). Written
 * package-local rather than imported — apps/web -> packages/command-engine
 * is the wrong dependency direction.
 */
function modifyInverseSnapshotUnchanged(
  prevSnapshot: DrawingEntity,
  next: unknown,
): boolean {
  if ((prevSnapshot as unknown) === next) return true;
  const n = next as { id?: unknown; type?: unknown };
  if (prevSnapshot.id !== n.id || prevSnapshot.type !== n.type) return false;
  return JSON.stringify(prevSnapshot) === JSON.stringify(next);
}

/**
 * D4 follow-up (@codex P2, PR #468 review) — pair a
 * `geometry/restore-viewport-clip` step onto the modify-command snapshot
 * inverse when the transient `shell/delete-entities` step it is about to
 * emit (see `buildArrayDeleteRestoreInverse`) would itself dangle a
 * SURVIVING viewport's clip. Unlike `geometry/delete-entity` /
 * `shell/delete-entities`'s OWN forward dispatch (which calls
 * `clearDanglingViewportClips` and threads the result into
 * `mutations.updated`, consumed by `buildViewportClipRestoreInverse`
 * above), a modify command's forward reducer (STRETCH/TRIM/…) never touches
 * Viewport entities, so no viewport diff ever reaches `mutations.updated`
 * here even when one of the entities being touched IN PLACE is currently a
 * live VPCLIP boundary. The referential cleanup instead fires SILENTLY at
 * UNDO-REPLAY time, when the constructed inverse's own
 * `shell/delete-entities` step runs for real and clears the surviving
 * viewport's clip — one step before `shell/restore-entities` re-adds the
 * boundary under the SAME id but leaves the viewport unclipped.
 *
 * Scans `preMutationDoc` — the SAME live document snapshot a real delete
 * dispatch would see at forward-time — for any Viewport whose clip boundary
 * handle is one of `ids` (the set this inverse is about to transiently
 * delete). Matching precedence mirrors `clearDanglingViewportClips`
 * (apps/web geometryReducerHelpers.ts) EXACTLY: a live typed
 * `clippingBoundaryHandle` is authoritative; the legacy `extraGroups`-340
 * carrier is consulted ONLY when there is no typed handle, and the
 * `NO_CLIP_HANDLE` ("0") sentinel never counts as a boundary reference —
 * the same three-carrier check `buildViewportClipRestoreInverse` already
 * documents mirroring. `ids` is `deleteIds` (added + updated) — never a
 * `removed` id: those never go through a delete step in the constructed
 * inverse (the forward already removed them), so they cannot re-trigger the
 * cleanup during replay.
 */
function buildDanglingClipRestoreForDeleteIds(
  preMutationDoc: DrawingDocument,
  ids: readonly string[],
): EditorCommand | null {
  if (ids.length === 0) return null;
  const norm = (h: string): string => h.trim().toLowerCase();
  const idSet = new Set(ids.map(norm));
  const viewports: Array<{ spaceId: string; viewport: DrawingEntity }> = [];
  for (const space of preMutationDoc.spaces) {
    for (const entity of space.entities) {
      if (entity.type !== "Viewport") continue;
      const vp = entity as unknown as ViewportEntity;
      const typedPresent = vp.clippingBoundaryHandle !== undefined;
      const typedDangles =
        typedPresent &&
        vp.clippingBoundaryHandle !== NO_CLIP_HANDLE &&
        idSet.has(norm(vp.clippingBoundaryHandle!));
      const legacyDangles =
        !typedPresent &&
        (vp.extraGroups?.some(
          (g) => g.code === 340 && idSet.has(norm(g.value)),
        ) ??
          false);
      if (!typedDangles && !legacyDangles) continue;
      viewports.push({ spaceId: space.id, viewport: cloneEntity(entity) });
    }
  }
  if (viewports.length === 0) return null;
  return {
    type: "geometry/restore-viewport-clip",
    source: "system",
    viewports,
  } as unknown as EditorCommand;
}

/**
 * D4 follow-up (@codex P2, PR #468 review) — wrap `inverseCmd` together with
 * `clipRestoreCmd` (if any) into a `history/replay-inverses` batch,
 * flattening onto an EXISTING batch's tail (the delete+restore two-step
 * `buildArrayDeleteRestoreInverse` already produces) rather than nesting a
 * batch-inside-a-batch. `null` clipRestoreCmd passes `inverseCmd` through
 * unchanged — the common case (no dangling clip).
 */
function withDanglingClipRestore(
  inverseCmd: EditorCommand,
  clipRestoreCmd: EditorCommand | null,
): EditorCommand {
  if (!clipRestoreCmd) return inverseCmd;
  const asBatch = inverseCmd as unknown as {
    type: string;
    batch?: EditorCommand[];
  };
  const existingBatch =
    asBatch.type === "history/replay-inverses" && Array.isArray(asBatch.batch)
      ? asBatch.batch
      : [inverseCmd];
  return {
    type: "history/replay-inverses",
    source: "system",
    batch: [...existingBatch, clipRestoreCmd],
  } as unknown as EditorCommand;
}

function buildModifyCommandSnapshotInverse(
  preMutationDoc: DrawingDocument,
  mutations: ForwardMutations | undefined,
): EditorCommand | null {
  if (!mutations) return null;

  const deleteIds: string[] = [];
  const restoreEntities: DrawingEntity[] = [];

  // Entities the forward MINTED (fillet arc, chamfer line, break's other
  // fragment, join's polyline, explode's members, …) — undo deletes them.
  for (const added of mutations.added) {
    deleteIds.push(added.entity.id);
  }
  // Entities the forward changed IN PLACE (id preserved) — undo deletes the
  // post-mutation shape and re-appends the pre-mutation snapshot. Codex P2
  // (PR #468) — skip a record whose `next` is STRUCTURALLY UNCHANGED from
  // the pre-mutation snapshot (a no-op STRETCH window, or an EXPLODE
  // document-rebuild reporting an unrelated survivor) — see
  // `modifyInverseSnapshotUnchanged`'s doc comment. Neither deleting nor
  // restoring an untouched entity keeps undo a no-op churn-free pass-through
  // for geometry the forward never actually changed.
  for (const updated of mutations.updated ?? []) {
    // @codex #496 P2 — a D4 modify reducer NEVER edits a Viewport directly (the
    // invariant this builder documents); the ONLY viewport diff that can reach
    // here is `recomputeObjectDerivedViewportClips` (apps/web) RE-DERIVING an
    // object-mode clip because THIS command moved its boundary. That boundary is a
    // NON-viewport `added`/`updated` id in the SAME mutation, so it lands in
    // `deleteIds` and `buildDanglingClipRestoreForDeleteIds` below restores the
    // viewport's clip from the PRE-mutation snapshot — a full snapshot-replace, the
    // SAME `geometry/restore-viewport-clip` the handle-only boundary case already
    // emits. Delete+restoring the viewport itself (it is not a modified target)
    // would be wrong, so skip it and let the dedicated clip-restore path own it.
    if (updated.next.type === "Viewport") {
      continue;
    }
    const prevSnapshot = findEntityById(preMutationDoc, updated.next.id);
    if (prevSnapshot && modifyInverseSnapshotUnchanged(prevSnapshot, updated.next)) {
      continue;
    }
    deleteIds.push(updated.next.id);
    if (prevSnapshot) restoreEntities.push(cloneEntity(prevSnapshot));
  }
  // Entities the forward REMOVED outright (join/explode sources, break's
  // whole-entity-consumed edge case) — undo re-appends the pre-mutation
  // snapshot (there is nothing to delete — the forward already removed it).
  for (const removed of mutations.removed ?? []) {
    const prevSnapshot = findEntityById(preMutationDoc, removed.entityId);
    if (prevSnapshot) restoreEntities.push(cloneEntity(prevSnapshot));
  }

  if (deleteIds.length === 0 && restoreEntities.length === 0) return null;

  // Codex P2 (PR #468) — computed BEFORE branching: any id about to be
  // transiently (delete+restore branch) or permanently (delete-only branch)
  // deleted might be a live VPCLIP boundary for a surviving viewport; see
  // `buildDanglingClipRestoreForDeleteIds`'s doc comment. `null` when
  // `deleteIds` is empty (restore-only branch below) or nothing dangles.
  const clipRestoreCmd = buildDanglingClipRestoreForDeleteIds(
    preMutationDoc,
    deleteIds,
  );

  if (restoreEntities.length === 0) {
    return withDanglingClipRestore(
      {
        type: "shell/delete-entities",
        source: "system",
        entityIds: deleteIds,
      } as unknown as EditorCommand,
      clipRestoreCmd,
    );
  }
  if (deleteIds.length === 0) {
    // Nothing was transiently deleted (every touched id came from
    // `removed`, already gone pre-inverse) — no dangling-clip risk to pair.
    return {
      type: "shell/restore-entities",
      source: "system",
      entities: restoreEntities,
      // Individual snapshots carry their OWN spaceId (read by the
      // shell/restore-entities reducer in preference to this fallback), so a
      // multi-space restore (theoretical for EXPLODE spanning spaces) is
      // still placed correctly per entity.
      spaceId: restoreEntities[0]!.spaceId,
    } as unknown as EditorCommand;
  }
  return withDanglingClipRestore(
    buildArrayDeleteRestoreInverse(
      deleteIds,
      restoreEntities,
      restoreEntities[0]!.spaceId,
    ),
    clipRestoreCmd,
  );
}

/**
 * Codex P2-2 (PR #403) — ONE-TRUTH "optional common key → default-on-absence"
 * map for the undo INVERSE. Two common object fields are absent-by-default on a
 * `DrawingEntity` (their schema default is omitted, NOT stored), so when a
 * forward apply INTRODUCES one (prev absent → next present) the restoring
 * inverse must carry the canonical DEFAULT string to return the entity to its
 * absent/default state — NOT omit the key (which would leave the new value in
 * place = an undo no-op for that field):
 *
 *   - "Linetype Scale" → "1"  (DXF $LTSCALE / group 48, `z.number().optional()`
 *     on the base schema for EVERY type — AutoCAD default 1.0).
 *   - "Thickness"      → "0"  (DXF group 39 — `.optional()` on Dimension, and
 *     conceptually default-0 on every thickness-bearing type — gated by
 *     `THICKNESS_BEARING_ENTITY_TYPES` so a non-bearing type mints no key).
 *
 * Shared by BOTH inverse paths so they handle an absent-prev optional key
 * IDENTICALLY: the large-drawing `computeInverse` fallback (`readCommonProps`,
 * default-restore for an OMITTED entity field) and the LIVE snapshot inverse
 * (`buildPropertiesApplyInverseFromSnapshots`, default-restore for a key absent
 * in `prev` but present in `next`). Returns `undefined` for a non-optional key
 * (Layer/Color/Linetype/Lineweight/Transparency are always present) or a
 * thickness on a non-bearing type (no stray key).
 */
export function optionalCommonKeyDefault(
  label: string,
  entityType?: string,
): string | undefined {
  if (label === "Linetype Scale") return "1";
  if (label === "Thickness") {
    // Only a schema thickness-bearing type carries a thickness default; a
    // non-bearing type (MText / Block / …) mints no stray Thickness key.
    if (entityType === undefined) return undefined;
    return THICKNESS_BEARING_ENTITY_TYPES.has(entityType) ? "0" : undefined;
  }
  return undefined;
}

/**
 * Pre/post property snapshot pair for one entity touched by a
 * `properties/apply` (sourced from the shell reducer's EntityPreview
 * `updated` mutation diff). `prev`/`next` are the FULL preview-property
 * maps (Layer/Color/… AND geometry keys like "Start X" / "Radius" /
 * "Vertices" / "Control points") before and after the apply.
 *
 * Codex P2-2 (PR #403) — `entityType` carries the target's EntityType so the
 * inverse can restore an INTRODUCED optional common key (e.g. "Linetype Scale")
 * to its schema DEFAULT via `optionalCommonKeyDefault` (thickness default is
 * type-gated). Optional: a caller that does not thread it still restores the
 * type-independent "Linetype Scale" default; only a type-gated key is skipped.
 */
export interface PropertyApplyPrevNext {
  entityId: string;
  prev: Record<string, string>;
  next: Record<string, string>;
  entityType?: string;
}

/**
 * GRIPS-SUBSYSTEM (§5.11) / Phase 4.1.f — build the restoring inverse for a
 * `properties/apply` from the pre/post EntityPreview property snapshots.
 *
 * The legacy `computeInverse` `properties/apply` branch only snapshots the
 * COMMON_PROP_FIELD keys (Layer/Color/Linetype/Lineweight/Transparency)
 * because those are the only keys backed by `DrawingEntity` base fields it
 * can read from the document. Entity-geometry keys (End X/Y, Radius,
 * Vertices, Control points, …) live ONLY on the `EntityPreview.properties`
 * bag, so a grip-drag or Properties-panel geometry edit produced a NULL
 * inverse → editorStore appended no history entry → Ctrl+Z was a no-op.
 *
 * This helper is the by-substance fix: it snapshots the PRIOR value of
 * EXACTLY the keys the apply CHANGED (diff prev vs next per entity) and
 * emits a restoring `properties/apply` carrying those prior values. It is
 * the same "snapshot whatever this apply mutated" one-truth inverse, so it
 * also lights up Properties-panel geometry undo (identical mechanism).
 *
 * Returns null when nothing actually changed (no-op apply — no history
 * entry), so an idle drag / re-apply of identical values pushes no entry.
 */
export function buildPropertiesApplyInverseFromSnapshots(
  pairs: ReadonlyArray<PropertyApplyPrevNext>,
): EditorCommand | null {
  const perEntityValues: Record<string, Record<string, string>> = {};
  let anyChanged = false;
  let homogeneousReference: string | null = null;
  let isHomogeneous = true;

  for (const { entityId, prev, next, entityType } of pairs) {
    const restore: Record<string, string> = {};
    // Snapshot the PRIOR value of every key whose value changed.
    for (const key of Object.keys(next)) {
      if (next[key] === prev[key]) continue;
      if (key in prev) {
        // Key existed before → restore its prior value verbatim.
        restore[key] = prev[key]!;
        continue;
      }
      // Codex P2-2 (PR #403) — the key was INTRODUCED by this apply (present in
      // `next`, ABSENT in `prev`). For an OPTIONAL common field (Linetype Scale /
      // Thickness) whose absence means "schema default", the inverse must
      // RESTORE THE DEFAULT — emitting nothing would leave the new value in
      // place (an undo no-op for that field). This is the SAME absent→default
      // logic the large-drawing `computeInverse` path applies in
      // `readCommonProps`; both inverse paths now use the shared
      // `optionalCommonKeyDefault` helper so they agree. A non-optional /
      // geometry key that is genuinely new is left omitted (the mutators do not
      // delete preview keys, so a geometry key always exists in both maps).
      const def = optionalCommonKeyDefault(key, entityType);
      if (def !== undefined) {
        restore[key] = def;
      }
    }
    if (Object.keys(restore).length === 0) continue;
    anyChanged = true;
    perEntityValues[entityId] = restore;

    const signature = JSON.stringify(restore);
    if (homogeneousReference === null) {
      homogeneousReference = signature;
    } else if (homogeneousReference !== signature) {
      isHomogeneous = false;
    }
  }

  if (!anyChanged) return null;

  const entityIds = Object.keys(perEntityValues);
  if (isHomogeneous) {
    return {
      type: "properties/apply",
      source: "system",
      entityIds,
      values: perEntityValues[entityIds[0]!]!,
    } as unknown as EditorCommand;
  }
  return {
    type: "properties/apply",
    source: "system",
    entityIds,
    values: {},
    perEntityValues,
  } as unknown as EditorCommand;
}

/**
 * Does this viewport carry a REAL (non-sentinel) clip artefact? Mirrors
 * `invalidateViewportClip`'s three-carrier check EXACTLY, including the
 * `NO_CLIP_HANDLE` ("0") sentinel exclusion: a literal `340 "0"` is the
 * documented "no spatial filter" sentinel (AcDbViewport 340 default="0"), NOT a
 * boundary reference, and `invalidateViewportClip` deliberately PRESERVES it
 * while clearing the real handle/bit. So when a real clip dangles and is
 * cleared, the `"0"` sentinel survives in `extraGroups`; without this exclusion
 * the restore-inverse builder would read that surviving sentinel as a live clip
 * (nextHasClip=true) and suppress `geometry/restore-viewport-clip`, leaving the
 * viewport unclipped after undo (Codex #324/#327 P2).
 */
export function viewportHasRealClip(vp: {
  clippingBoundaryHandle?: string;
  statusBitFlags?: number;
  extraGroups?: ReadonlyArray<{ code: number; value: string }>;
}): boolean {
  const hasRealTypedHandle =
    vp.clippingBoundaryHandle !== undefined &&
    vp.clippingBoundaryHandle !== NO_CLIP_HANDLE;
  const hasNonRectBit =
    ((vp.statusBitFlags ?? 0) & VIEWPORT_NONRECT_CLIP_BIT) !== 0;
  const hasRealLegacy340 =
    vp.extraGroups?.some(
      (g) => g.code === 340 && g.value !== NO_CLIP_HANDLE,
    ) ?? false;
  return hasRealTypedHandle || hasNonRectBit || hasRealLegacy340;
}

/**
 * Restore half of the DELETE inverse for a dangling viewport clip (PR #324 P1
 * / #318 R2). When the deleted boundary entity B1 was a viewport's clip path,
 * the delete reducer (`clearDanglingViewportClips`) clears the SURVIVING
 * viewport's clip in place — dropping `clippingBoundaryHandle`, the group-90
 * `0x10000` non-rect bit, and the legacy `extraGroups`-340 carrier — and
 * reports it as an `updated` diff (prev = clipped VP, next = cleared VP). The
 * existing delete inverse only re-adds the deleted boundary, so without this
 * the viewport stays cleared after undo and the non-rectangular clip is lost.
 *
 * This scans `mutations.updated` for viewports whose PREV carried a clip that
 * NEXT lacks, and emits a single `geometry/restore-viewport-clip` carrying the
 * FULL prev snapshots (preserve-source: re-set the exact pre-delete VP, not an
 * inferred reconstruction). Returns `null` when no clip dangled so the delete
 * fast path (bare restore) is preserved. The prev snapshot is deep-cloned via
 * `cloneEntity` so the inverse command owns an immutable copy.
 */
function buildViewportClipRestoreInverse(
  updated: ForwardMutations["updated"],
): EditorCommand | null {
  if (!updated || updated.length === 0) return null;
  const viewports: Array<{ spaceId: string; viewport: DrawingEntity }> = [];
  for (const entry of updated) {
    const prev = entry.prev;
    if (!prev || prev.type !== "Viewport") continue;
    // Did prev carry a REAL clip? (typed 340 OR group-90 non-rect bit OR a
    // legacy extraGroups-340 carrier — the same three carriers
    // invalidateViewportClip strips, with the SAME `NO_CLIP_HANDLE` ("0")
    // sentinel exclusion, so a literal `340 "0"` never registers as a clip.)
    if (!viewportHasRealClip(prev)) continue;
    // …and was it actually CLEARED in next? (Guard against a no-clear update —
    // e.g. an unrelated viewport edit that happens to ride in `updated`.) The
    // `"0"` sentinel that invalidateViewportClip preserves in `next` must NOT
    // count as a live clip here, or undo would re-add the boundary but leave
    // the viewport unclipped (Codex #324/#327 P2).
    const next = entry.next;
    if (viewportHasRealClip(next)) continue;
    const spaceId = entry.spaceId;
    if (spaceId === undefined) continue;
    // prev is the runtime full DrawingEntity (EntityMutation.updated.prev),
    // structurally narrowed to ViewportClipFields here; clone the full snapshot.
    viewports.push({ spaceId, viewport: cloneEntity(prev as unknown as DrawingEntity) });
  }
  if (viewports.length === 0) return null;
  return {
    type: "geometry/restore-viewport-clip",
    source: "system",
    viewports,
  } as unknown as EditorCommand;
}

export function computeInverse(
  command: EditorCommand,
  doc: DrawingDocument,
  mutations?: ForwardMutations,
): EditorCommand | null {
  if (!hasGeometryInverseFamily(command.type)) return null;

  // Detect geometry/delete-entity by cast (now in editorCommandSchema but
  // TypeScript's discriminated union switch still works via casting).
  const asDelete = command as unknown as GeometryDeleteEntityCommand;
  if (asDelete.type === "geometry/delete-entity") {
    // P1-3 + P1-5: emit geometry/restore-entity with full entity snapshot.
    // This preserves the original entity id so redo (re-delete) can find
    // the entity by the same id after undo.
    const entity = findEntityById(doc, asDelete.entityId);
    // PR #324 P1 — if the delete cleared a dangling viewport clip (the deleted
    // entity was a viewport's clip boundary), the bare restore-entity loses the
    // non-rectangular clip on undo. Pair it with a restore-viewport-clip in a
    // history/replay-inverses batch so undo re-adds the boundary AND re-sets the
    // survivor's clip from its prev snapshot. Boundary-first (mirrors
    // buildArrayDeleteRestoreInverse ordering).
    const restoreEntityCmd: EditorCommand | null = entity
      ? ({
          type: "geometry/restore-entity",
          source: "system",
          entity: cloneEntity(entity),
          spaceId: findEntitySpaceId(doc, asDelete.entityId) ?? entity.spaceId,
        } as unknown as EditorCommand)
      : null;
    const clipRestoreCmd = buildViewportClipRestoreInverse(mutations?.updated);
    if (clipRestoreCmd) {
      // Both pieces → compound batch (boundary first); only the clip restore →
      // return it directly (don't emit a one-element batch); neither → null.
      if (restoreEntityCmd) {
        return {
          type: "history/replay-inverses",
          source: "system",
          batch: [restoreEntityCmd, clipRestoreCmd],
        } as unknown as EditorCommand;
      }
      return clipRestoreCmd;
    }
    // No dangling clip — preserve the bare-restore fast path (null if the
    // entity was not found and nothing else changed).
    return restoreEntityCmd;
  }

  // P1-4: handle shell/delete-entities → shell/restore-entities.
  const asShellDelete = command as unknown as ShellDeleteEntitiesCommand;
  if (asShellDelete.type === "shell/delete-entities") {
    const entities: DrawingEntity[] = [];
    // Determine spaceId from the first found entity (all deleted entities
    // must be in the same doc, though they can span multiple spaces — for
    // multi-space deletes, spaceId is the first found entity's space.
    // shell/restore-entities uses spaceId as the target space for entities
    // that don't carry their own spaceId override).
    let spaceId: string | undefined;
    // FU2 (#305 sweep) — entityIds is a plain array (no schema uniqueness);
    // dedupe so a repeated id yields ONE snapshot — shell/restore-entities
    // blind-appends each snapshot, so duplicates would corrupt the document
    // with two same-id entities after undo.
    for (const id of new Set(asShellDelete.entityIds)) {
      const entity = findEntityById(doc, id);
      if (entity) {
        entities.push(cloneEntity(entity));
        if (!spaceId) spaceId = findEntitySpaceId(doc, id) ?? entity.spaceId;
      }
    }
    const restoreEntitiesCmd: EditorCommand | null =
      entities.length === 0
        ? null
        : ({
            type: "shell/restore-entities",
            source: "system",
            entities,
            spaceId: spaceId ?? entities[0]!.spaceId,
          } as unknown as EditorCommand);
    // PR #324 P1 — same dangling-viewport-clip pairing as geometry/delete-entity:
    // wrap [restore-entities, restore-viewport-clip] in a replay-inverses batch
    // (boundary first) when a survivor's clip was cleared.
    const clipRestoreCmd = buildViewportClipRestoreInverse(mutations?.updated);
    if (clipRestoreCmd) {
      if (restoreEntitiesCmd) {
        return {
          type: "history/replay-inverses",
          source: "system",
          batch: [restoreEntitiesCmd, clipRestoreCmd],
        } as unknown as EditorCommand;
      }
      return clipRestoreCmd;
    }
    return restoreEntitiesCmd;
  }

  // Parametric inverses for modify commands (move/rotate/scale/mirror/offset).
  // These are pure mathematical inverses — no snapshot needed.
  switch (command.type) {
    // BASE / $INSBASE — restore the PRIOR drawing insertion base point. `doc`
    // here is the PRE-mutation document (the store passes preMutationDoc for
    // this command — see editorStore.ts `needsPreMutationDoc`), so
    // `doc.insertionBase` is the value before BASE ran. Returning a
    // `header/set-insertion-base` carrying that prior point makes undo a single
    // atomic step. No-op (null) when the base is unchanged so an idle BASE does
    // not push an empty history entry.
    case "header/set-insertion-base": {
      const cmd = command as unknown as {
        type: "header/set-insertion-base";
        source: CommandSource;
        point: { x: number; y: number; z: number };
      };
      const prior = doc.insertionBase ?? { x: 0, y: 0, z: 0 };
      const priorZ = prior.z ?? 0;
      const nextZ = cmd.point.z ?? 0;
      if (
        prior.x === cmd.point.x &&
        prior.y === cmd.point.y &&
        priorZ === nextZ
      ) {
        return null;
      }
      return {
        type: "header/set-insertion-base",
        source: "system",
        point: { x: prior.x, y: prior.y, z: priorZ },
      } as unknown as EditorCommand;
    }
    // CECOLOR / COLOR / ЦВЕТ — restore the PRIOR current colour ($CECOLOR). `doc`
    // here is the PRE-mutation document (editorStore needsPreMutationDoc gates on
    // this command), so `doc.cecolor` is the value before COLOR ran. Returning a
    // `header/set-cecolor` carrying that prior colour makes undo a single atomic
    // step — the exact BASE precedent above. No-op (null) when the colour is
    // unchanged (compared via the lossless `serializeColor` codec so true-colour /
    // colour-book current colours compare correctly) so an idle COLOR does not
    // push an empty history entry.
    case "header/set-cecolor": {
      const cmd = command as unknown as {
        type: "header/set-cecolor";
        source: CommandSource;
        color: EntityColor;
      };
      const prior: EntityColor = doc.cecolor ?? { kind: "byLayer" };
      if (serializeColor(prior) === serializeColor(cmd.color)) {
        return null;
      }
      return {
        type: "header/set-cecolor",
        source: "system",
        color: prior,
      } as unknown as EditorCommand;
    }
    case "geometry/move-entities": {
      const cmd = command as unknown as {
        type: "geometry/move-entities";
        source: string;
        entityIds: string[];
        delta: { x: number; y: number };
      };
      if (cmd.delta.x === 0 && cmd.delta.y === 0) return null;
      return { ...cmd, delta: { x: -cmd.delta.x, y: -cmd.delta.y } } as unknown as EditorCommand;
    }
    case "geometry/rotate-entities": {
      const cmd = command as unknown as {
        type: "geometry/rotate-entities";
        source: string;
        entityIds: string[];
        basePoint: { x: number; y: number };
        angleDeg: number;
        restoreAttributeRotations?: Record<string, Record<string, number | null>>;
      };
      if (cmd.angleDeg === 0) return null;
      // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2,
      // round 4) — ROTATE's angle arithmetic is exactly self-inverse
      // (`+angleDeg` then `-angleDeg`), but `withWorldAttributeRotation`'s
      // presence-preserving write-back cannot, from the mutated entity
      // ALONE, distinguish "this attribute started genuinely absent and the
      // forward rotate materialised it" from "this attribute already had an
      // explicit value" — both look identical (present) by the time the
      // blind angle-negation undo runs. Snapshot the PRE-rotate
      // `overrideRotation` of every WORLD-placed (`overridePosition` set)
      // Block attribute so the reducer can restore it EXACTLY (`null` =
      // was absent → delete back to absent), same null-sentinel convention
      // as `geometry/mirror-entities`' `restoreAttributeRotations`.
      const restoreAttributeRotations: Record<
        string,
        Record<string, number | null>
      > = {};
      // GitHub Codex review, PR #558 P2 — resolve every selected id in ONE
      // O(total) pass (`findEntitiesByIds`) instead of one `findEntityById`
      // call per id (each of which re-scans the whole document), which made
      // this loop O(selected × total).
      const entitiesById = findEntitiesByIds(doc, cmd.entityIds);
      for (const id of cmd.entityIds) {
        const entity = entitiesById.get(id) as
          | {
              type?: string;
              attributes?: Array<{
                overrideRotation?: number;
                overridePosition?: { x: number; y: number };
              }>;
            }
          | undefined;
        // targeted-cad-mutation-testing EQUIVALENT MUTANT (round 4 verified
        // survivor): ConditionalExpression forcing `entity.type === "Block"`
        // → `true`. Why equivalent: same argument as the sibling capture in
        // the `geometry/mirror-entities` case below — `blockInsertEntity
        // Schema` is the ONLY `DrawingEntity` member declaring an
        // `attributes` field (verified: no other entity schema in
        // `model.ts` declares it), so a schema-parsed non-Block entity's
        // `.attributes` is structurally always `undefined` —
        // `Array.isArray(undefined)` is `false` regardless of the type
        // check's outcome, making the type check unable to change the
        // guard's result for any realistic (schema-valid) `entity`.
        if (entity && entity.type === "Block" && Array.isArray(entity.attributes)) {
          const perAttribute: Record<string, number | null> = {};
          entity.attributes.forEach((attr, index) => {
            if (attr.overridePosition !== undefined) {
              perAttribute[String(index)] = attr.overrideRotation ?? null;
            }
          });
          if (Object.keys(perAttribute).length > 0) {
            restoreAttributeRotations[id] = perAttribute;
          }
        }
      }
      const inverse: Record<string, unknown> = {
        ...cmd,
        angleDeg: -cmd.angleDeg,
      };
      if (Object.keys(restoreAttributeRotations).length > 0) {
        inverse.restoreAttributeRotations = restoreAttributeRotations;
      } else {
        delete inverse.restoreAttributeRotations;
      }
      return inverse as unknown as EditorCommand;
    }
    case "geometry/scale-entities": {
      const cmd = command as unknown as {
        type: "geometry/scale-entities";
        source: string;
        entityIds: string[];
        basePoint: { x: number; y: number };
        factor: number;
        restoreAttributeHeights?: Record<
          string,
          Record<string, number | null>
        >;
        restoreAttributeRotations?: Record<
          string,
          Record<string, number | null>
        >;
        restoreMtextMeasurements?: Record<
          string,
          { horizontalWidth: number; verticalHeight: number }
        >;
      };
      if (cmd.factor === 1) return null;
      // GitHub Codex review, PR #558 round 7 P2 — SCALE's own factor
      // arithmetic is exactly self-inverse (`factor` then `1 / factor`),
      // but `scaleEntityRadii`'s presence-preserving `overrideHeight`
      // write-back (round 6 P2, `presencePreservingValue`) cannot, from the
      // mutated entity ALONE, distinguish "this attribute started
      // genuinely absent and the forward scale materialised it" from "this
      // attribute already had an explicit value" — both look identical
      // (present) by the time the blind factor-inversion undo runs.
      // Snapshot the PRE-scale `overrideHeight` of every WORLD-placed
      // (`overridePosition` set) Block attribute so the reducer can
      // restore it EXACTLY (`null` = was absent → delete back to absent),
      // same null-sentinel convention as `geometry/rotate-entities`'
      // `restoreAttributeRotations`.
      const restoreAttributeHeights: Record<
        string,
        Record<string, number | null>
      > = {};
      // GitHub Codex review PR #558 round 2 (round-14 local read-only
      // follow-up) P2 — the SAME presence ambiguity `restoreAttributeHeights`
      // resolves for `overrideHeight`, now for `overrideRotation`: a
      // negative `factor` bumps rotation 180° (`scaleEntityRadii.ts`'s own
      // doc), and `presencePreservingRotation`'s materialise-on-change rule
      // means the mutated entity ALONE cannot tell "started absent, forward
      // scale materialised it" from "already had an explicit value" once
      // undo's blind `1 / factor` re-applies the SAME 180°-bump rule (still
      // negative, since `1 / negative` stays negative). Snapshot ONLY
      // matters when the forward factor is negative — a positive-factor
      // scale never touches rotation at all, matching
      // `scaleEntityRadii.ts`'s own `factor < 0` gate.
      const restoreAttributeRotations: Record<
        string,
        Record<string, number | null>
      > = {};
      // GitHub Codex review PR #794 round 16 P2 — issue #804. The forward
      // SCALE carries an MTEXT's groups 42/43 with the block it resizes, but
      // CLEARS them when the scaled block is not geometrically SIMILAR (an
      // absolute inline `\H` run paints at a world height the scale does not
      // move). The scaled case is self-inverse through `factor` then
      // `1 / factor`, exactly as `nominalHeight` is; the CLEARED case is not
      // invertible at all — the inverse re-dispatch would multiply 0 by
      // `1 / factor` and the measurement would be gone for good. Snapshot the
      // pre-scale pair of precisely the entities EITHER LEG of the round trip
      // is about to clear.
      //
      // BOTH LEGS, not just the forward — @codex PR #794 round 21,
      // 4057969957. Until round 21 this snapshotted what the FORWARD clears,
      // under the claim that "the SAME predicate decides, so the snapshot and
      // the clear cannot disagree". They can: the undo leg asks the predicate
      // about a DIFFERENT entity (the SCALED one, nominal `h × |k|`) at a
      // DIFFERENT factor (`1 / |k|`), so a guard can refuse there and not
      // here — and then the undo clears a pair this map never captured and
      // the measurement is gone for good. Round 21 makes the predicate's own
      // DOMAIN symmetric under that swap (`drawing-model::
      // mtextScaleSimilarity`, THE DOMAIN IS SYMMETRIC), which removes the
      // mechanism; it cannot make it EXACT, because the undo's source nominal
      // is `h × |k|` and multiplying that back by `1 / |k|` is a float round
      // trip that can step over a threshold. So the verdict is asked in BOTH
      // directions and a snapshot is taken if EITHER clears — and, since
      // round 22, in both REPRESENTATIONS of the undo row (double and the
      // `Float32Array` the compact store commits it to; see AND ON THE ROW AS
      // IT WILL BE STORED below). The extra probes are paid only by an entity
      // that CARRIES a measurement and that the forward would keep; a restore
      // entry on an entity the undo would have scaled correctly is not dead
      // weight but the exact pre-scale pair, which is what undo owes in any
      // case.
      const restoreMtextMeasurements: Record<
        string,
        { horizontalWidth: number; verticalHeight: number }
      > = {};
      const absFactor = Math.abs(cmd.factor);
      /** Would the round trip clear this row's pair on EITHER leg? */
      const roundTripClearsMeasurement = (
        mtext: MtextMeasurementCapture,
      ): boolean => {
        const nominalHeight = mtext.nominalHeight;
        // targeted-cad-mutation-testing EQUIVALENT MUTANT: ConditionalExpression
        // on this `typeof` TEST → `false`, i.e. the guard never fires. ONLY
        // the test — the `true` it returns is NOT equivalent, see the end of
        // this block. Why the TEST is: a skipped guard lands in one of two
        // cases, and neither is observable.
        //   1. `absFactor !== 1` — the very next line asks
        //      `mtextScaleSimilarity` about the SAME row, and `nominalHeight`
        //      is one of its two schema-REQUIRED fields: a non-number is not
        //      judgeable and answers `false` there, so the next line returns
        //      the same `true` this guard would have. Same verdict, one more
        //      call.
        //   2. `absFactor === 1` — the verdict genuinely DIFFERS: the
        //      predicate answers `true` from its no-op early-out BEFORE it
        //      ever reads the nominal, both at `|k|` and at `1 / |k| === 1` on
        //      the constructed undo row (whose nominal is `NaN`), so the
        //      skipped guard makes this predicate answer `false` and no
        //      snapshot is taken. It costs nothing: at `|k| === 1`
        //      `scaleEntityRadii`'s MTEXT arm multiplies 42/43 by 1 and
        //      carries the pair UNCHANGED on both legs, so a restore entry
        //      would have nothing to put back. (`factor === 1` returns null
        //      above; `factor === -1` is the reachable form.)
        // The BooleanLiteral mutant of the `true` below — `return false` — is
        // NOT equivalent and is NOT suppressed: in case 1 the pair really is
        // cleared, and dropping the snapshot destroys the measurement across
        // undo, which is #804 itself. It is killed by
        // `geometryInverse.test.ts`'s "MUTATION REGRESSION — a row with NO
        // numeric group 40 is SNAPSHOTTED, because the forward CLEARS it",
        // with the `|factor| === 1` complement pinned by the sibling case
        // right after it. The test is written out rather than left implicit
        // because the UNDO-leg call below has to CONSTRUCT the scaled row and
        // needs the number.
        if (typeof nominalHeight !== "number") return true;
        if (!mtextUniformScalePreservesLayoutShape(mtext, absFactor)) {
          return true;
        }
        // The UNDO leg, on the entity `scaleEntityRadii` is about to write:
        // same contents, same columns, `nominalHeight × absFactor` — the one
        // field that arm changes that this predicate reads — at the factor
        // the inverse command will carry. `1 / absFactor` is the same double
        // the undo dispatch computes: the inverse's factor is `1 / cmd.factor`
        // and `scaleEntityRadii` takes `Math.abs` of it, and IEEE division is
        // sign-symmetric, so `|1 / cmd.factor|` and `1 / |cmd.factor|` are the
        // identical value for both signs of the forward factor.
        const inverseFactor = 1 / absFactor;
        const undoNominal = nominalHeight * absFactor;
        if (
          !mtextUniformScalePreservesLayoutShape(
            { ...mtext, nominalHeight: undoNominal },
            inverseFactor,
          )
        ) {
          return true;
        }
        // …AND ON THE ROW AS IT WILL BE STORED — @codex PR #794 round 22, one
        // representation level below round 21. The line above models the undo
        // row in DOUBLES, but the row undo actually reads has been through the
        // document's compact store, whose numeric lanes are `Float32Array`
        // (`apps/web/.../compactStores/compactTextStores.ts`, MTEXT float lane
        // 2 is `nominalHeight`). THE WITNESS: at the band's own upper edge
        // `h = 1e6` under `k = 1/3`, the double undo nominal is
        // 333333.3333333333 and `× 3` is EXACTLY 1e6, in band — so the double
        // model says the undo carries. Stored, that height is 333333.34375,
        // and `× 3` is 1000000.03125, ABOVE the band: the real undo refuses,
        // clears 42/43, and without this probe there is no snapshot to put
        // them back. The disagreement is made by the STORAGE, not by the
        // predicate, so no threshold can be moved to remove it.
        //
        // WHY THE UNION AND NOT A REPLACEMENT. Not every document row comes
        // from a compact store (a freshly parsed/imported document is plain
        // doubles), so both representations are live and the verdict is taken
        // as "clears on EITHER". That is the conservative direction and it is
        // free of risk: a snapshot that turns out unnecessary merely restores
        // the values the round trip would have reproduced anyway, while a
        // missing one loses the measurement for good.
        //
        // WHICH LANES ARE RE-ROUNDED, AND WHY THE REST NEED NOT BE — @codex
        // PR #794 round 23 P2. Round 22 re-rounded the NOMINAL alone and
        // argued that was enough because the scale MOVES no other field. That
        // argument is FALSE: what decides is not which fields the scale
        // moves, it is which fields the STORE quantises and the predicate
        // then READS. Taking `CompactMtextStore`'s MTEXT lanes
        // (`apps/web/.../compactStores/compactTextStores.ts::appendMtext`)
        // against `MtextLayoutShapeInput`, the predicate's own read set:
        //   * `nominalHeight` — float32 (lane 2), consumed NUMERICALLY.
        //     Re-rounded, at the PRODUCT the undo row will hold.
        //   * `lineSpacingFactor` — float32 (lane 5), consumed NUMERICALLY:
        //     it is the walk's line-drop multiplier
        //     (`mtextScaleSimilarity.ts::probeLayout` feeds it to
        //     `mtextPaintedLayout` at all three heights). Re-rounded. THE
        //     WITNESS: a finite `3.5e38` — above the float32 maximum
        //     ≈3.4028235e38 — is stored as `Infinity`, the aggregates stop
        //     being finite, the real undo refuses and clears 42/43. Both
        //     double-modelled verdicts carry, so without this lane there is
        //     no snapshot to put the pair back. It is NOT a presence flag:
        //     rounding it changes the walk, not merely a branch.
        //   * `columnWidth` / `columnGutter` — float32 (lanes 6, 7), but the
        //     predicate only tests them `!== 0`. Rounding them CANNOT move
        //     this verdict, and that is a proof rather than a preference: a
        //     row whose column lengths are non-zero was already REFUSED by
        //     the double-model probe two statements above (same fields, same
        //     zero test) and returned `true` from this helper before reaching
        //     here, while a row whose column lengths are zero keeps them:
        //     `Math.fround(0) === 0`.
        //     The only rounding they can suffer — a subnormal flushing TO
        //     zero — is therefore invisible here and, in the double model,
        //     pushes toward refusing, i.e. toward snapshotting: the safe
        //     direction under the union.
        //   * `columnType` is an `Uint16Array` lane and `columnHeights` is
        //     Map-backed (`store.columnHeights.set`), so neither is a float32
        //     at all; `contents` is an interned string; the 42/43 pair itself
        //     is Map-backed and exact.
        // The DESTINATION height needs no separate treatment: the undo's
        // destination is computed BY the predicate from the nominal handed to
        // it, so feeding the stored nominal makes it exact, and the FORWARD
        // leg's destination is never re-read after storage —
        // `scaleEntityRadii` decides the forward clear from the same doubles
        // in the same pass, before anything is committed.
        //
        // ABSENT STAYS ABSENT. `lineSpacingFactor` is optional on
        // `MtextLayoutShapeInput` and the predicate reads it as
        // `?? 1`; `Math.fround(undefined)` is `NaN`, which would poison the
        // walk. So the override is built ONLY for a row that has the number,
        // and a row without one is handed to the probe exactly as absent.
        //
        // COST, measured the way `mtextScaleSimilarity`'s own COST section
        // measures (node 22 under `tsx`, a 47-character MTEXT with an inline
        // `\H` run, 10 000 calls after a 2 000-call warm-up): round 22
        // reported this helper's predicate path going from ≈190 ms to
        // ≈285 ms per 10 000 entities when the third probe runs — ≈+9 µs per
        // formatted, MEASURED MTEXT, one more probe where two were already
        // paid. ROUND 23 CHANGES THAT NUMBER BY NOTHING MEASURABLE, and the
        // delta was re-run rather than asserted: the round-22 tail and this
        // one, benched head to head on the same box and the same shape with
        // the third probe forced (46-character MTEXT with an inline `\H`
        // run, `h = 1e6` under `k = 1/3`, three interleaved repetitions),
        // come out 190.1 / 188.8, 188.3 / 189.2 and 191.1 / 186.5 ms per
        // 10 000 — a spread that straddles zero, because one `typeof` and one
        // `Math.fround` sit beside a walk that parses `contents` three times
        // over. Nothing else pays it: an MTEXT with no measurement never
        // reaches this helper, one with no backslash leaves the predicate on
        // its early-out, either earlier probe REFUSING already returned
        // `true` from this helper, and the guard below skips it outright
        // whenever storage moves NO lane the predicate reads — which, for a
        // store-backed row (every lane already a float32 by construction)
        // under any dyadic factor (×2, ×0.5, ×0.25: the overwhelmingly
        // common SCALE), it never does, since shifting a float32's exponent
        // is exact.
        const storedUndoNominal = Math.fround(undoNominal);
        const storedLineSpacing =
          typeof mtext.lineSpacingFactor === "number"
            ? { lineSpacingFactor: Math.fround(mtext.lineSpacingFactor) }
            : undefined;
        // A pure short-circuit, on the COMPLETE modelled row and not on the
        // nominal alone: when storage moves NO lane the predicate reads, the
        // two models are the SAME call. (The two mutants that merely disarm
        // this guard are equivalent — see the block below the return; every
        // other mutant on it is killed.)
        if (
          storedUndoNominal === undoNominal &&
          storedLineSpacing?.lineSpacingFactor === mtext.lineSpacingFactor
        ) {
          return false;
        }
        // targeted-cad-mutation-testing EQUIVALENT MUTANT — ONE behaviour
        // wearing two faces, and the ONLY suppression here:
        // ConditionalExpression on the short-circuit test above → `false`,
        // and BlockStatement on its body → `{}`. Both mean the guard never
        // short-circuits. Why equivalent: its ONLY job is to skip a call
        // whose arguments are identical to the one just made.
        // `mtextUniformScalePreservesLayoutShape` is pure — it reads fields
        // off its input and parses `contents`, with no cache and no state
        // (`drawing-model::mtextScaleSimilarity`) — so when the modelled row
        // equals the double-modelled one the skipped call returns exactly
        // what the call above returned, `true` (a `false` there already
        // returned). Verified AT the `absFactor === 1` boundary rather than
        // assumed away from it: there the predicate answers `true` from its
        // no-op early-out BEFORE reading the nominal OR the spacing factor,
        // so the extra call answers `true` for ANY modelled row and the
        // verdict is `false` either way. (`factor === 1` returns null above;
        // `factor === -1` is the reachable form.)
        //
        // NOTHING ELSE HERE IS SUPPRESSED, and round 23 does not inherit
        // round 22's claim that flipping `===` to `!==` is a wash. Each
        // comparison now guards a DIFFERENT lane, so each `!==` (and the
        // `&&` → `||`, and forcing the test `true`) short-circuits away the
        // stored-row probe for exactly the rows that need it: the nominal
        // comparison is killed by the 1e6 / `k = 1/3` witness, the spacing
        // comparison by the `3.5e38` one, both in "ROUND TRIP — the
        // COMPACT-STORE witness…" in `geometryInverse.test.ts`. The `typeof`
        // ternary is not suppressed either: forcing it `true` feeds the probe
        // `Math.fround(undefined)`, killed by "MUTATION REGRESSION — a row
        // with NO `lineSpacingFactor`…"; forcing it `false` is round 22's
        // defect verbatim. (Stryker emits NO mutant for a `Math.fround` call,
        // so both conversions rely on those witnesses' exact values instead.)
        return !mtextUniformScalePreservesLayoutShape(
          { ...mtext, ...storedLineSpacing, nominalHeight: storedUndoNominal },
          inverseFactor,
        );
      };
      const entitiesById = findEntitiesByIds(doc, cmd.entityIds);
      for (const id of cmd.entityIds) {
        const mtext = entitiesById.get(id) as MtextMeasurementCapture | undefined;
        if (
          mtext?.type === "MText" &&
          ((mtext.horizontalWidth ?? 0) !== 0 || (mtext.verticalHeight ?? 0) !== 0) &&
          // The ROW ITSELF, with no caller-side defaults — the same call the
          // forward pass makes with its typed entity, so the two verdicts are
          // one code path — and the UNDO leg's call as well.
          roundTripClearsMeasurement(mtext)
        ) {
          restoreMtextMeasurements[id] = {
            horizontalWidth: mtext.horizontalWidth ?? 0,
            verticalHeight: mtext.verticalHeight ?? 0,
          };
        }
        const entity = entitiesById.get(id) as
          | {
              type?: string;
              attributes?: Array<{
                overrideHeight?: number;
                overrideRotation?: number;
                overridePosition?: { x: number; y: number };
              }>;
            }
          | undefined;
        // targeted-cad-mutation-testing EQUIVALENT MUTANT: ConditionalExpression
        // forcing `entity.type === "Block"` → `true`. Why equivalent: same
        // argument as the sibling `geometry/rotate-entities` capture above
        // — `blockInsertEntitySchema` is the ONLY `DrawingEntity` member
        // declaring an `attributes` field, so a schema-parsed non-Block
        // entity's `.attributes` is structurally always `undefined` —
        // `Array.isArray(undefined)` is `false` regardless of the type
        // check's outcome.
        if (entity && entity.type === "Block" && Array.isArray(entity.attributes)) {
          const perAttribute: Record<string, number | null> = {};
          const perAttributeRotation: Record<string, number | null> = {};
          // targeted-cad-mutation-testing EQUIVALENT MUTANT (round-14
          // verified survivor): EqualityOperator `cmd.factor < 0` →
          // `cmd.factor <= 0`. Why equivalent: `geometry/scale-entities`'
          // own command schema (`packages/contracts/src/editor.ts`) refines
          // `factor` with `f !== 0 && Number.isFinite(f) && Math.abs(f) >
          // FLOAT_EPSILON` — `factor === 0` is REJECTED before this code
          // ever runs (the SAME invariant `scaleEntityRadii.ts`'s own
          // `factor < 0` gate relies on), so `< 0` and `<= 0` partition the
          // SAME two non-empty regions for every value this snapshot loop
          // can actually receive.
          entity.attributes.forEach((attr, index) => {
            if (attr.overridePosition !== undefined) {
              perAttribute[String(index)] = attr.overrideHeight ?? null;
              if (cmd.factor < 0) {
                perAttributeRotation[String(index)] = attr.overrideRotation ?? null;
              }
            }
          });
          if (Object.keys(perAttribute).length > 0) {
            restoreAttributeHeights[id] = perAttribute;
          }
          if (Object.keys(perAttributeRotation).length > 0) {
            restoreAttributeRotations[id] = perAttributeRotation;
          }
        }
      }
      const inverse: Record<string, unknown> = {
        ...cmd,
        factor: 1 / cmd.factor,
      };
      if (Object.keys(restoreAttributeHeights).length > 0) {
        inverse.restoreAttributeHeights = restoreAttributeHeights;
      } else {
        delete inverse.restoreAttributeHeights;
      }
      if (Object.keys(restoreAttributeRotations).length > 0) {
        inverse.restoreAttributeRotations = restoreAttributeRotations;
      } else {
        delete inverse.restoreAttributeRotations;
      }
      if (Object.keys(restoreMtextMeasurements).length > 0) {
        inverse.restoreMtextMeasurements = restoreMtextMeasurements;
      } else {
        delete inverse.restoreMtextMeasurements;
      }
      return inverse as unknown as EditorCommand;
    }
    case "geometry/mirror-entities": {
      const cmd = command as unknown as {
        type: "geometry/mirror-entities";
        source: string;
        entityIds: string[];
        mirrorLine1: { x: number; y: number };
        mirrorLine2: { x: number; y: number };
        deleteSource: boolean;
        // MIRRTEXT rides along on the inverse re-mirror.
        mirrtext?: number;
        restoreRotations?: Record<string, number>;
        restoreXAxisDirs?: Record<
          string,
          { x: number; y: number; z: number }
        >;
        // Codex #342 F3 — undo-restore map for MTEXT extrusion handedness. A null
        // value means the source MTEXT had NO extrusion field at all (undo must
        // delete it back to absent, not re-emit (0,0,1)).
        restoreExtrusions?: Record<
          string,
          { x: number; y: number; z: number } | null
        >;
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — per-attribute undo-restore map
        // for a Block entity's WORLD `overrideRotation` values (entityId →
        // { [attribute array index as a string]: original overrideRotation,
        // or `null` when the source attribute had NO overrideRotation field
        // at all — undo must delete it back to absent, not re-emit `0`,
        // mirroring `restoreExtrusions`'s null-sentinel convention).
        restoreAttributeRotations?: Record<
          string,
          Record<string, number | null>
        >;
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex review P2) — per-
        // annotative-context undo-restore map for a Multileader entity's
        // `annotativeContexts[].textRotation` (entityId → { [context array
        // index as a string]: original textRotation }).
        restoreContextRotations?: Record<string, Record<string, number>>;
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
        // per-attribute undo-restore map for a Block entity's WORLD
        // `overrideGenerationFlags` (handedness companion to
        // `restoreAttributeRotations`, same null-sentinel shape).
        restoreAttributeGenerationFlags?: Record<
          string,
          Record<string, number | null>
        >;
        // GitHub Codex review PR #558 P2 — per-entity undo-restore map for a
        // Multileader's OR a Dimension's `textMirrored` (entityId →
        // original value, `null` = the source had no field at all). Shared
        // across both entity types — ids never collide.
        restoreTextMirrored?: Record<string, boolean | null>;
      };
      const dx = cmd.mirrorLine2.x - cmd.mirrorLine1.x;
      const dy = cmd.mirrorLine2.y - cmd.mirrorLine1.y;
      const lenSq = dx * dx + dy * dy;
      if (cmd.deleteSource) {
        // deleteSource=true: forward no-ops on degenerate axis (mirrorPoint returns original);
        // inverse must also no-op. Use same threshold as mirrorPoint forward.
        if (lenSq < MIRROR_AXIS_DEGENERATE_LENSQ) return null;
        // The COORDINATE mirror (positions) is an exact involution, so the
        // inverse is a re-mirror across the same axis (carrying `mirrtext`).
        // BUT the rotation / direction re-derivation is NOT always self-inverse,
        // for BOTH MIRRTEXT modes — so snapshot the PRE-mutation rotation +
        // direction of every text entity this command touches and carry them in
        // `restoreRotations` / `restoreXAxisDirs`; the reducer overwrites them
        // AFTER the (exact) position re-mirror, giving a bit-exact undo.
        //
        //   MIRRTEXT=0 (readable): the forward re-derives a forward-reading
        //     rotation, which for a baseline pointing backward (rotation ∈
        //     (90°,270°)) lands a plain re-mirror 180° off — corrupting text
        //     orientation on undo (SCOPE item 4). It also recomputes an MTEXT's
        //     `xAxisDirection` (group 11/21/31), likewise not self-inverse.
        //
        //   MIRRTEXT=1 (full geometric, Codex #342 round-3 FINDING A): TEXT and
        //     default-direction MTEXT are genuinely self-inverse, but a
        //     CUSTOM-direction MTEXT is NOT bit-exact: the forward stores the
        //     reflected angle in BOTH `rotation` and the (still-custom)
        //     `xAxisDirection`; the inverse re-reads the angle from the custom
        //     direction (ezdxf get_rotation precedence), so the VECTOR returns
        //     to the original but the raw `rotation` drifts (e.g. 0 → 2θ−α →
        //     stays non-zero). The writer emits group 50 for any truthy
        //     `rotation` (dxfBridge.entityConversion.ts ~1018), so the drift adds
        //     a spurious group 50 the source file never had — a round-trip
        //     byte-change. Capturing the original rotation+vector for MIRRTEXT=1
        //     too makes undo bit-exact for every text type/axis. The capture is a
        //     harmless no-op for the already-self-inverse TEXT / default-dir
        //     MTEXT (restores the same value).
        //
        // `doc` here is the PRE-mutation document (editorStore passes
        // preMutationDoc for deleteSource=true mirrors).
        const restoreRotations: Record<string, number> = {};
        const restoreXAxisDirs: Record<
          string,
          { x: number; y: number; z: number }
        > = {};
        // Codex #342 F3: snapshot the pre-mutation MTEXT extrusion handedness so
        // undo restores it exactly. A `null` entry records that the source had NO
        // extrusion field (undo deletes it back to absent — bit-exact round-trip,
        // no spurious (0,0,1)). MIRRTEXT=1 sets the extrusion to (0,0,−1); the
        // Z-flip is involutive, but a source MTEXT that started absent would
        // otherwise undo to (0,0,1)·flip = present, so the snapshot is required.
        const restoreExtrusions: Record<
          string,
          { x: number; y: number; z: number } | null
        > = {};
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — snapshot for a Block's
        // per-attribute WORLD `overrideRotation` (index-keyed; see the
        // command-type doc comment above).
        const restoreAttributeRotations: Record<
          string,
          Record<string, number | null>
        > = {};
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex review P2) —
        // snapshot for a Multileader's per-annotative-context `textRotation`
        // (index-keyed; see the command-type doc comment above).
        const restoreContextRotations: Record<
          string,
          Record<string, number>
        > = {};
        // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review
        // P2, round 4) — snapshot for a Multileader's per-scale SECONDARY
        // CONTEXT_DATA blocks' `context.mTextData.rotation` (index-keyed;
        // `null` = the source block's MTextData had no rotation field at
        // all — see the command-type doc comment above).
        const restoreSecondaryContextRotations: Record<
          string,
          Record<string, number | null>
        > = {};
        // GitHub Codex review PR #558 round 2 P2 — snapshot for the SAME
        // secondary CONTEXT_DATA blocks' pre-mutation `mTextData.extrusion`
        // (OCS handedness), keyed by array index alongside rotation — `null`
        // records that the block's MTextData carried NO extrusion field at
        // all (undo restores absence, never a spurious `{0,0,1}`). Captured
        // unconditionally (like `restoreSecondaryContextRotations`, not
        // gated on the forward command's `mirrtext` — harmless-if-unchanged
        // for MIRRTEXT=0, which never touches extrusion).
        const restoreSecondaryContextExtrusions: Record<
          string,
          Record<string, { x: number; y: number; z: number } | null>
        > = {};
        // GitHub Codex review PR #558 round 2 (round-20 local read-only
        // follow-up) P2 — "Restore primary MLeader extrusion state on
        // undo". `entity.contextData.mTextData.extrusion` (the PRIMARY
        // context, distinct from `secondaryContexts` above) is now
        // properly REFLECTED by the reducer (round-17's own fix) instead
        // of canonicalised — but `reflectMLeaderExtrusion`'s presence-
        // preserving OMIT-when-canonical-default rule means a source that
        // EXPLICITLY carried the canonical `{0,0,1}` (present, even though
        // it equals the implied default — a real byte-faithful DXF
        // group-210/220/230 the source file had) would, after a mirror +
        // undo round-trip, land back at the SAME `{0,0,1}` value but with
        // the field OMITTED instead of explicitly present — the SAME
        // presence-ambiguity class every other snapshot in this command
        // already resolves. `null` records genuine absence (undo deletes
        // back to absent); a real vector records the EXACT source value
        // (undo restores it explicitly, even if it happens to equal the
        // canonical default).
        const restorePrimaryContextExtrusion: Record<
          string,
          { x: number; y: number; z: number } | null
        > = {};
        // GitHub Codex review PR #558 (formal bot review, HEAD 4fefb166)
        // P2 — "Restore MLeader text directions exactly on undo". Same
        // presence-ambiguity AND floating-point-drift class as
        // `restorePrimaryContextExtrusion`/`restoreSecondaryContext
        // Extrusions` immediately above: `reflectVectorXY` is involutive
        // in EXACT arithmetic but NOT bit-exact for a non-axis-aligned
        // mirror axis (trigonometric round-trip drift, ~1e-16 scale) — a
        // blind re-reflection on undo would leave a tiny but genuine
        // byte-fidelity regression in the re-exported DXF group 13/23/33,
        // and (independently) cannot distinguish "was absent, materialized
        // on forward" from "was already explicit" the same way every other
        // presence-preserving snapshot in this command already resolves.
        // `null` records genuine absence; a real vector records the EXACT
        // source value.
        const restoreSecondaryContextTextDirections: Record<
          string,
          Record<string, { x: number; y: number; z: number } | null>
        > = {};
        const restorePrimaryContextTextDirection: Record<
          string,
          { x: number; y: number; z: number } | null
        > = {};
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
        // snapshot for a Block's per-attribute WORLD `overrideGenerationFlags`
        // (index-keyed, null-sentinel — same shape/rationale as
        // `restoreAttributeRotations` immediately above: the handedness XOR
        // is value-self-inverse, but presence is NOT — a blind re-XOR of an
        // originally-ABSENT field materializes an explicit `0` instead of
        // returning to absent, so undo needs the snapshot exactly like
        // rotation does).
        const restoreAttributeGenerationFlags: Record<
          string,
          Record<string, number | null>
        > = {};
        // GitHub Codex review PR #558 P2 — snapshot for a Multileader's OR
        // a Dimension's entity-level `textMirrored` (one boolean per
        // entity, null-sentinel = the source had no `textMirrored` field at
        // all — undo must restore absence, not an explicit `false`, same
        // presence-vs-value rationale as the Block map above).
        const restoreTextMirrored: Record<string, boolean | null> = {};
        // GitHub Codex review, PR #558 P2 — same O(total) single-pass
        // resolution as `geometry/rotate-entities`' sibling loop above
        // (`findEntitiesByIds`), instead of one `findEntityById` call per
        // id inside this loop.
        const entitiesById = findEntitiesByIds(doc, cmd.entityIds);
        for (const id of cmd.entityIds) {
          const entity = entitiesById.get(id) as
            | {
                type?: string;
                rotation?: number;
                textRotation?: number;
                textMirrored?: boolean;
                textAngleRelativeToDimLine?: number;
                xAxisDirection?: { x: number; y: number; z?: number };
                extrusion?: { x: number; y: number; z?: number };
                annotativeContexts?: Array<{ textRotation?: number }>;
                attributes?: Array<{
                  overrideRotation?: number;
                  overrideGenerationFlags?: number;
                  overridePosition?: { x: number; y: number };
                }>;
                secondaryContexts?: Array<{
                  context: {
                    mTextData?: {
                      rotation?: number;
                      extrusion?: { x: number; y: number; z: number };
                      textDirection?: { x: number; y: number; z: number };
                    };
                  };
                }>;
                // GitHub Codex review PR #558 round 2 (round-20 local
                // read-only follow-up) P2 — the PRIMARY (structural)
                // CONTEXT_DATA, distinct from `secondaryContexts` above.
                contextData?: {
                  mTextData?: {
                    extrusion?: { x: number; y: number; z: number };
                    textDirection?: { x: number; y: number; z: number };
                  };
                };
              }
            | undefined;
          if (
            entity &&
            (entity.type === "Text" || entity.type === "MText") &&
            typeof entity.rotation === "number"
          ) {
            restoreRotations[id] = entity.rotation;
          }
          // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — Multileader carries its
          // MIRRTEXT-gated angle in `textRotation` (a bare scalar, no
          // companion vector), NOT `rotation`. Same `restoreRotations` map —
          // the reducer branches on `mirrored.type` to pick the field name.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANTS (skill-mandated
          // format) for the two ConditionalExpression-forced-`true` mutants on
          // this guard's sub-expressions:
          //   1. `entity.type === "Multileader"` → `true`.
          //      Why equivalent: `multileaderEntitySchema` is the ONLY
          //      `DrawingEntity` member declaring a `textRotation` field
          //      (`packages/drawing-model/src/model.ts` — verified: no other
          //      entity schema declares it; `dimensionEntitySchema` ALSO
          //      declares one, but Dimension is captured by the SEPARATE
          //      `textAngleRelativeToDimLine` guard below — GitHub Codex
          //      review PR #558 P1 redirected Dimension's MIRRTEXT target
          //      to the real DXF group-53 field, group 52's `textRotation`
          //      has no renderer consumer). Zod object schemas strip
          //      unrecognised keys by default, so a schema-parsed non-
          //      Multileader entity's `.textRotation` is structurally always
          //      `undefined` — forcing the type check to `true` cannot change
          //      the OUTER `&&`'s outcome for any realistic (schema-valid)
          //      `entity`, because the trailing `typeof === "number"` check
          //      below is false regardless.
          //   2. `typeof entity.textRotation === "number"` → `true`.
          //      Why equivalent: `multileaderEntitySchema.textRotation`
          //      carries `.default(0)` (never `.optional()`), so once the
          //      type is confirmed by the sibling condition, `textRotation`
          //      is a number by schema construction on every parsed
          //      document — forcing this check to `true` cannot change the
          //      outcome for an entity that already passed the type check.
          //   Both proven via `findEntityById` reading from a
          //   `drawingDocumentSchema.parse()`-validated `DrawingDocument`
          //   (never a raw/unvalidated object) — the type-narrowing invariant
          //   the schema module itself enforces.
          if (
            entity &&
            entity.type === "Multileader" &&
            typeof entity.textRotation === "number"
          ) {
            restoreRotations[id] = entity.textRotation;
          }
          // GitHub Codex review PR #558 P1 — Dimension carries its
          // MIRRTEXT-gated angle in `textAngleRelativeToDimLine` (DXF group
          // 53, ezdxf's real `text_rotation`), NOT `textRotation` (group 52,
          // ezdxf's `oblique` — a different field, untouched by MIRRTEXT).
          // Same `restoreRotations` map (entityId → one scalar) as the
          // Multileader case above — the reducer branches on `mirrored.type`
          // to pick the field name back on undo.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANTS: same shape as
          // the Multileader guard above. `dimensionEntitySchema` is the ONLY
          // `DrawingEntity` member declaring `textAngleRelativeToDimLine`,
          // and it carries `.default(0)` (never `.optional()`) — so both a
          // forced-`true` type check and a forced-`true` `typeof` check are
          // no-ops for any schema-valid entity that already satisfies the
          // OTHER half of this guard.
          if (
            entity &&
            entity.type === "Dimension" &&
            typeof entity.textAngleRelativeToDimLine === "number"
          ) {
            restoreRotations[id] = entity.textAngleRelativeToDimLine;
          }
          // Only MTEXT carries a direction vector; capture it verbatim so undo
          // restores the exact original (including a non-default vector that
          // the forward mirror reflected).
          if (entity && entity.type === "MText" && entity.xAxisDirection) {
            restoreXAxisDirs[id] = {
              x: entity.xAxisDirection.x,
              y: entity.xAxisDirection.y,
              z: entity.xAxisDirection.z ?? 0,
            };
          }
          // Codex #342 F3: capture the MTEXT extrusion handedness verbatim
          // (null = the source had no extrusion field — undo must restore absence,
          // not (0,0,1)). Only MTEXT carries this in the mirror path.
          if (entity && entity.type === "MText") {
            restoreExtrusions[id] = entity.extrusion
              ? {
                  x: entity.extrusion.x,
                  y: entity.extrusion.y,
                  z: entity.extrusion.z ?? 1,
                }
              : null;
          }
          // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — snapshot every WORLD-placed
          // attribute's (`overridePosition` set — NOT `overrideRotation`;
          // local Codex read-only review P1, see `withWorldAttributeRotation`'s
          // doc for the full regime rationale) pre-mutation `overrideRotation`,
          // keyed by its array index. `null` records that the source had NO
          // `overrideRotation` field at all — undo must delete it back to
          // absent, not re-emit `0` (byte-faithful DXF ATTRIB group-50
          // round-trip, same convention as `restoreExtrusions` above). A
          // block-local attribute (`overridePosition` unset — including one
          // that happens to carry its OWN `overrideRotation` from a value-only
          // EATTEDIT edit) is not gated by MIRRTEXT here — see the reducer/
          // `transformEntityCoords` doc comments — so it needs no snapshot.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANT: ConditionalExpression
          // forcing `entity.type === "Block"` → `true`. Why equivalent:
          // `blockInsertEntitySchema` is the ONLY `DrawingEntity` member
          // declaring an `attributes` field (verified: no other entity schema
          // in `model.ts` declares it), so a schema-parsed non-Block entity's
          // `.attributes` is structurally always `undefined` — `Array.isArray
          // (undefined)` is `false` regardless of the type check's outcome,
          // making the type check unable to change the guard's result for any
          // realistic (schema-valid) `entity`.
          if (entity && entity.type === "Block" && Array.isArray(entity.attributes)) {
            const perAttribute: Record<string, number | null> = {};
            const perAttributeFlags: Record<string, number | null> = {};
            entity.attributes.forEach((attr, index) => {
              if (attr.overridePosition !== undefined) {
                perAttribute[String(index)] = attr.overrideRotation ?? null;
                // GitHub Codex review PR #558 P2 — same index-keyed snapshot
                // as `overrideRotation` above, for its handedness companion.
                perAttributeFlags[String(index)] =
                  attr.overrideGenerationFlags ?? null;
              }
            });
            if (Object.keys(perAttribute).length > 0) {
              restoreAttributeRotations[id] = perAttribute;
            }
            if (Object.keys(perAttributeFlags).length > 0) {
              restoreAttributeGenerationFlags[id] = perAttributeFlags;
            }
          }
          // GitHub Codex review PR #558 P2 — snapshot a Multileader's
          // pre-mutation entity-level `textMirrored` (one boolean per
          // entity — handedness is document-wide, not per-annotative-scale;
          // see `multileaderFromEntity.ts`'s doc for why no per-context
          // variant exists).
          if (entity && entity.type === "Multileader") {
            restoreTextMirrored[id] = entity.textMirrored ?? null;
          }
          // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — a
          // Dimension's own `textMirrored` (handedness companion to
          // `textAngleRelativeToDimLine`) shares the SAME
          // `restoreTextMirrored` map — entityId-keyed, and a Dimension id
          // and a Multileader id in the SAME mirror batch never collide.
          if (entity && entity.type === "Dimension") {
            restoreTextMirrored[id] = entity.textMirrored ?? null;
          }
          // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex review P2) —
          // snapshot every annotative context's pre-mutation `textRotation`,
          // keyed by its array index (the render prefers an active context's
          // OWN rotation over the flat field, `multileaderFromEntity.ts`).
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANTS:
          //   1. ConditionalExpression forcing `entity.type === "Multileader"`
          //      → `true`. Why equivalent: SEVERAL other entity kinds also
          //      declare an `annotativeContexts` array (Text/MText/Hatch/
          //      Attdef/…), but they use the DIFFERENT discriminated-union
          //      `annotScaleContextSchema` (`packages/drawing-model/src/
          //      annotative.ts`) — every one of its 8 `kind` variants (text /
          //      mtext / mtext_attribute / attribute / blockref / hatch /
          //      dimension / multileader-context) carries `rotation`,
          //      `patternAngle`, or NO angle field at all — NONE declares a
          //      field literally named `textRotation` (only Multileader's OWN
          //      `mleaderContextDataSchema` does). So for a schema-valid non-
          //      Multileader entity, `ctx.textRotation` is structurally always
          //      `undefined` regardless of the type check's outcome — the
          //      trailing `typeof === "number"` check (next equivalence)
          //      excludes it either way.
          //   2. ConditionalExpression forcing `typeof ctx.textRotation ===
          //      "number"` → `true`. Why equivalent:
          //      `mleaderContextDataSchema.textRotation` carries `.default(0)`
          //      (never `.optional()`), so once the outer type check confirms
          //      a real Multileader, every one of its context entries already
          //      has a real number — forcing this check to `true` cannot
          //      change the outcome for an entry that already passed the
          //      outer guard.
          if (
            entity &&
            entity.type === "Multileader" &&
            Array.isArray(entity.annotativeContexts)
          ) {
            const perContext: Record<string, number> = {};
            entity.annotativeContexts.forEach((ctx, index) => {
              if (typeof ctx.textRotation === "number") {
                perContext[String(index)] = ctx.textRotation;
              }
            });
            if (Object.keys(perContext).length > 0) {
              restoreContextRotations[id] = perContext;
            }
          }
          // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review
          // P2, round 4) — snapshot every secondary CONTEXT_DATA block's
          // pre-mutation `mTextData.rotation`, keyed by its array index.
          // `null` records that the block's MTextData carried NO rotation
          // field at all (`mleaderMTextDataSchema.rotation` is `.optional()`,
          // never `.default()`) — undo must delete it back to absent, not
          // re-emit `0` (same convention as `restoreAttributeRotations`).
          // Blocks with no MTextData at all (BlockData-content secondaries)
          // are skipped entirely — there is no rotation field to snapshot.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANT (round 4
          // verified survivor): ConditionalExpression forcing
          // `entity.type === "Multileader"` → `true`. Why equivalent:
          // `secondaryContexts` is declared EXACTLY ONCE in `model.ts`,
          // inside `multileaderEntitySchema` only — no other `DrawingEntity`
          // member carries a field literally named `secondaryContexts`, so
          // a schema-parsed non-Multileader entity's `.secondaryContexts` is
          // structurally always `undefined` — `Array.isArray(undefined)` is
          // `false` regardless of the type check's outcome, making the type
          // check unable to change the guard's result for any realistic
          // (schema-valid) `entity`.
          if (
            entity &&
            entity.type === "Multileader" &&
            Array.isArray(entity.secondaryContexts)
          ) {
            const perSecondary: Record<string, number | null> = {};
            const perSecondaryExtrusion: Record<
              string,
              { x: number; y: number; z: number } | null
            > = {};
            const perSecondaryTextDirection: Record<
              string,
              { x: number; y: number; z: number } | null
            > = {};
            entity.secondaryContexts.forEach((sec, index) => {
              if (sec.context.mTextData) {
                perSecondary[String(index)] =
                  sec.context.mTextData.rotation ?? null;
                // GitHub Codex review PR #558 round 2 P2 — same index, same
                // MTextData-presence guard as rotation above.
                perSecondaryExtrusion[String(index)] =
                  sec.context.mTextData.extrusion ?? null;
                // GitHub Codex review PR #558 (formal bot review, HEAD
                // 4fefb166) P2 — same index, same guard, for textDirection.
                perSecondaryTextDirection[String(index)] =
                  sec.context.mTextData.textDirection ?? null;
              }
            });
            if (Object.keys(perSecondary).length > 0) {
              restoreSecondaryContextRotations[id] = perSecondary;
            }
            if (Object.keys(perSecondaryExtrusion).length > 0) {
              restoreSecondaryContextExtrusions[id] = perSecondaryExtrusion;
            }
            if (Object.keys(perSecondaryTextDirection).length > 0) {
              restoreSecondaryContextTextDirections[id] =
                perSecondaryTextDirection;
            }
          }
          // GitHub Codex review PR #558 round 2 (round-20 local read-only
          // follow-up) P2 — snapshot the PRIMARY context's pre-mutation
          // `mTextData.extrusion`, same null-sentinel convention as every
          // sibling snapshot above.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANT (round 20
          // verified survivor): ConditionalExpression forcing
          // `entity.type === "Multileader"` → `true`. Why equivalent: same
          // invariant class as the `secondaryContexts` guard above —
          // `contextData` is declared EXACTLY ONCE in `model.ts`, inside
          // `multileaderEntitySchema` only (no other `DrawingEntity` member
          // carries a field literally named `contextData`), so a
          // schema-parsed non-Multileader entity's `.contextData` is
          // structurally always `undefined` — `entity.contextData?.mTextData`
          // is falsy regardless of the type check's outcome, making the type
          // check unable to change the guard's result for any realistic
          // (schema-valid) `entity`.
          if (entity && entity.type === "Multileader" && entity.contextData?.mTextData) {
            restorePrimaryContextExtrusion[id] =
              entity.contextData.mTextData.extrusion ?? null;
            // GitHub Codex review PR #558 (formal bot review, HEAD
            // 4fefb166) P2 — same guard, for the primary context's
            // textDirection.
            restorePrimaryContextTextDirection[id] =
              entity.contextData.mTextData.textDirection ?? null;
          }
        }
        const inverse: Record<string, unknown> = { ...cmd };
        if (Object.keys(restoreRotations).length > 0) {
          inverse.restoreRotations = restoreRotations;
        } else {
          // No readable-text rotation to restore — drop any stale map so the
          // inverse is a clean self-inverse re-mirror (and redo, which re-runs
          // the FORWARD, never carries restoreRotations).
          delete inverse.restoreRotations;
        }
        if (Object.keys(restoreXAxisDirs).length > 0) {
          inverse.restoreXAxisDirs = restoreXAxisDirs;
        } else {
          delete inverse.restoreXAxisDirs;
        }
        if (Object.keys(restoreExtrusions).length > 0) {
          inverse.restoreExtrusions = restoreExtrusions;
        } else {
          delete inverse.restoreExtrusions;
        }
        if (Object.keys(restoreAttributeRotations).length > 0) {
          inverse.restoreAttributeRotations = restoreAttributeRotations;
        } else {
          delete inverse.restoreAttributeRotations;
        }
        if (Object.keys(restoreContextRotations).length > 0) {
          inverse.restoreContextRotations = restoreContextRotations;
        } else {
          delete inverse.restoreContextRotations;
        }
        if (Object.keys(restoreSecondaryContextRotations).length > 0) {
          inverse.restoreSecondaryContextRotations =
            restoreSecondaryContextRotations;
        } else {
          delete inverse.restoreSecondaryContextRotations;
        }
        if (Object.keys(restoreSecondaryContextExtrusions).length > 0) {
          inverse.restoreSecondaryContextExtrusions =
            restoreSecondaryContextExtrusions;
        } else {
          delete inverse.restoreSecondaryContextExtrusions;
        }
        if (Object.keys(restorePrimaryContextExtrusion).length > 0) {
          inverse.restorePrimaryContextExtrusion =
            restorePrimaryContextExtrusion;
        } else {
          delete inverse.restorePrimaryContextExtrusion;
        }
        if (Object.keys(restoreSecondaryContextTextDirections).length > 0) {
          inverse.restoreSecondaryContextTextDirections =
            restoreSecondaryContextTextDirections;
        } else {
          delete inverse.restoreSecondaryContextTextDirections;
        }
        if (Object.keys(restorePrimaryContextTextDirection).length > 0) {
          inverse.restorePrimaryContextTextDirection =
            restorePrimaryContextTextDirection;
        } else {
          delete inverse.restorePrimaryContextTextDirection;
        }
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
        // handedness restore maps, same present-or-drop pattern as every
        // sibling above.
        if (Object.keys(restoreAttributeGenerationFlags).length > 0) {
          inverse.restoreAttributeGenerationFlags = restoreAttributeGenerationFlags;
        } else {
          delete inverse.restoreAttributeGenerationFlags;
        }
        if (Object.keys(restoreTextMirrored).length > 0) {
          inverse.restoreTextMirrored = restoreTextMirrored;
        } else {
          delete inverse.restoreTextMirrored;
        }
        return inverse as unknown as EditorCommand;
      }
      // deleteSource=false: forward creates copies even with near-degenerate axis
      // (copies land at same position as originals). Inverse = delete those copies.
      if (!mutations || mutations.added.length === 0) return null;
      const mirrorCopyIds = mutations.added.map((a) => a.entity.id);
      return {
        type: "shell/delete-entities",
        source: "system",
        entityIds: mirrorCopyIds,
      } as unknown as EditorCommand;
    }
    case "geometry/offset-entity": {
      // Offset creates a new copy entity. Inverse = delete the created copy.
      // Requires mutations.added to know the copy id.
      if (!mutations || mutations.added.length === 0) return null;
      const offsetCopyIds = mutations.added.map((a) => a.entity.id);
      return {
        type: "shell/delete-entities",
        source: "system",
        entityIds: offsetCopyIds,
      } as unknown as EditorCommand;
    }
    case "geometry/copy": {
      // AutoCAD COPY (base + second point) appends one clone of every source
      // entity per dispatch. Inverse = delete the appended clones — same
      // pattern as the deleteSource=false mirror / offset copy inverses.
      // Requires mutations.added to know the clone ids (the source entities
      // are NOT touched, so there is nothing to restore — only the clones to
      // remove). A zero-delta COPY still appends coincident clones, so we do
      // NOT short-circuit on a degenerate displacement.
      if (!mutations || mutations.added.length === 0) return null;
      const copyCloneIds = mutations.added.map((a) => a.entity.id);
      return {
        type: "shell/delete-entities",
        source: "system",
        entityIds: copyCloneIds,
      } as unknown as EditorCommand;
    }
    // Phase MC5 — viewport mutation inverses. Each snapshot is read from the
    // pre-mutation document (doc = preMutationDoc at call site) so that undo
    // restores the exact prior VP state. readViewportOverrides merges entity
    // fields + XDATA overrides, giving the authoritative pre-mutation state.
    case "geometry/viewport-set-lock": {
      const cmd = command as unknown as {
        type: "geometry/viewport-set-lock";
        source: CommandSource;
        viewportId: string;
        locked: boolean;
      };
      const vp = findViewportInDoc(doc, cmd.viewportId);
      if (!vp) return null;
      // Read pre-mutation overrides from XDATA so XDATA-locked VPs round-trip
      // correctly even when vp.displayLocked is stale / not yet synced.
      const preOverrides = readViewportOverrides(vp);
      return {
        ...cmd,
        locked: (vp.displayLocked ?? false) || preOverrides.locked,
      } as unknown as EditorCommand;
    }
    case "geometry/viewport-set-shadeplot": {
      // Undo of a shade-plot mode change. shadePlotMode is a first-class DXF
      // group-170 entity field (NOT XDATA), so the prior value is read directly
      // off the pre-mutation viewport and carried VERBATIM — NO `?? 0`.
      //
      // PRESERVE ABSENCE: a viewport that had NO group 170 (model
      // `shadePlotMode === undefined`) edited then undone must return to
      // `undefined`, NOT 0. Collapsing undefined→0 here would make a later save
      // gain a spurious `170=0` (the writer emits group 170 whenever
      // `shadePlotMode !== undefined`, writer.ts ~1104). The contract now accepts
      // an OPTIONAL `shadePlotMode` (editor.ts) precisely so this inverse can
      // carry `undefined` to RESTORE the absent record; the forward mutation
      // treats `undefined` as "clear the field".
      //
      // No clamp, and the prior is GUARANTEED `undefined` or 0..3: the forward
      // `applyViewportSetShadePlot` only mutates (and so only produces an
      // undoable command for) a viewport already in 0..3 (undefined counts as 0) —
      // a viewport in an imported Visual Style mode 4 / Render Preset mode 5 is a
      // no-op there (it never enters history), so no inverse is ever built for it.
      // The prior value carried here is therefore always within the now-optional
      // 0..3 schema (Fix 1, editor.ts), which always re-accepts the inverse on
      // undo. (The imported 4/5 round-trips verbatim through parse → export, never
      // through this command — the deferred 348/333 visual-style subsystem owns
      // its editing; see docs/test-gaps/viewport-shadeplot-visualstyle-modes.md.)
      const cmd = command as unknown as {
        type: "geometry/viewport-set-shadeplot";
        source: CommandSource;
        viewportId: string;
        shadePlotMode: number | undefined;
      };
      const vp = findViewportInDoc(doc, cmd.viewportId);
      if (!vp) return null;
      // NO-OP MIRROR: `applyViewportSetShadePlot` refuses to mutate (returns
      // mutated:false) when (a) the target is the paper-space control viewport
      // (vp.viewportId === 1) or (b) its CURRENT mode is outside the authorable
      // base range 0..3 (an imported Visual Style 4 / Render Preset 5 —
      // `undefined` counts as 0, in range). When the forward command does not
      // mutate, returning a non-null inverse here would record a PHANTOM undo
      // entry (and a 4/5 prior would be an out-of-range inverse the optional 0..3
      // schema rejects). Return null in those cases so no history entry is built.
      const priorMode = vp.shadePlotMode ?? 0;
      if (vp.viewportId === 1 || priorMode < 0 || priorMode > 3) {
        return null;
      }
      const prior = vp.shadePlotMode;
      return {
        ...cmd,
        shadePlotMode: prior,
      } as unknown as EditorCommand;
    }
    case "geometry/viewport-vplayer": {
      const cmd = command as unknown as {
        type: "geometry/viewport-vplayer";
        source: CommandSource;
        viewportId: string;
        action: "freeze" | "thaw";
        layerName: string;
      };
      // Inverse of freeze is thaw and vice versa. `doc` here is the PRE-mutation
      // document (same contract the shade-plot `prior` snapshot relies on), so
      // snapshot the viewport's CURRENT native group-331 carrier as the exact
      // list to restore on undo. A forward thaw drops the thawed layer's handle
      // POSITIVELY, and the plain freeze inverse re-derives+appends it lossily;
      // carrying the verbatim prior list restores the source ORDER/CASING for an
      // imported viewport (@codex #486, preserve-source-never-infer). Always
      // co-emitted (value or null) so the forward takes the restore path.
      //
      // `sourceFrozenLayerHandles ?? frozenLayerHandles` (@codex #514 P2 "Preserve
      // source handles in VPLAYER undo snapshots"): for an imported/reconciled
      // viewport, `frozenLayerHandles` alone has ALREADY been reduced to only the
      // unresolved/dangling survivors (the double-count-safe carrier the
      // agent-bridge projector reads — @codex #486 P2); the FULL verbatim 331
      // (order + casing) lives on `sourceFrozenLayerHandles` instead (@codex #514
      // P2 "Preserve verbatim 331 order before clearing handles"). Snapshotting
      // `frozenLayerHandles` alone would restore only the danglers on undo,
      // losing the resolved handles' exact source order/casing — the SAME
      // fallback the export path (`dxfBridge.entityConversion.ts`) already uses.
      const vp = findViewportInDoc(doc, cmd.viewportId);
      const priorHandles = vp?.sourceFrozenLayerHandles ?? vp?.frozenLayerHandles;
      return {
        ...cmd,
        action: cmd.action === "freeze" ? "thaw" : "freeze",
        restoreFrozenLayerHandles:
          priorHandles && priorHandles.length > 0 ? [...priorHandles] : null,
      } as unknown as EditorCommand;
    }
    case "geometry/viewport-clip-set": {
      // VPCLIP RECTANGULAR set — `applyViewportClipSet` not only reshapes the
      // view rectangle (center/width/height + rescaled viewHeight, dropped
      // childEntityIds) but ALSO invalidates any PRIOR non-rectangular clip
      // (polygon / imported SPATIAL_FILTER) via `invalidateViewportClip`. A
      // bounds-only inverse would restore the rectangle but LOSE the cleared
      // non-rect clip (@codex #496 P2), so the faithful inverse is a full
      // pre-mutation snapshot restore (which also restores the exact bounds).
      const cmd = command as unknown as {
        type: "geometry/viewport-clip-set";
        source: CommandSource;
        viewportId: string;
      };
      return buildViewportClipSnapshotInverse(doc, cmd.viewportId, cmd.source);
    }
    case "geometry/viewport-clip-set-polygon": {
      // VPCLIP polygonal AUTHORING — the forward supersedes the viewport's
      // WHOLE clip state (none / rectangular reshape / imported SPATIAL_FILTER /
      // another polygon → the new polygon), so the faithful inverse is a
      // FULL-SNAPSHOT restore of the PRE-mutation viewport (preserve-source, no
      // field inferred): re-set the entire viewport verbatim via the existing
      // system-only `geometry/restore-viewport-clip` (restores
      // clipBoundaryPaperSpace + clipPath + statusBitFlags + any 340 carrier).
      const cmd = command as unknown as {
        type: "geometry/viewport-clip-set-polygon";
        source: CommandSource;
        viewportId: string;
        boundary?: ReadonlyArray<{ x: number; y: number }>;
        boundaryEntityId?: string;
      };
      // NO-OP MIRROR (@codex #496 P2) — `applyViewportClipSetPolygon` refuses to
      // mutate (returns mutated:false, document unchanged) whenever the boundary
      // cannot author a valid clip: a missing / open / wrong-space
      // `boundaryEntityId`, a curved boundary type, or a degenerate
      // (<3-vertex / zero-area) polygon. In those cases returning the
      // full-snapshot inverse would record a PHANTOM undo entry AND clear redo
      // for ANY already-clipped viewport, so a FAILED VPCLIP would consume an
      // undo step / discard redo despite NO document change. Gate the inverse
      // with the SAME shared resolver the forward authors with (against the SAME
      // pre-mutation `doc`), so the inverse is built if and ONLY IF the forward
      // genuinely mutated. (The viewport-not-found / control-viewport no-ops are
      // already covered inside `buildViewportClipSnapshotInverse`.)
      if (
        !resolveVpclipBoundaryPolygon(doc, cmd.viewportId, {
          boundary: cmd.boundary,
          boundaryEntityId: cmd.boundaryEntityId,
        })
      ) {
        return null;
      }
      return buildViewportClipSnapshotInverse(doc, cmd.viewportId, cmd.source);
    }
    case "geometry/viewport-clip-remove": {
      // VPCLIP Delete — `applyViewportClipRemove` invalidates every persisted
      // clip artefact (typed 340 handle, group-90 non-rect bit, legacy
      // extraGroups-340, resolved clipPath, paper boundary) AND detaches the
      // OBJECTS SPATIAL_FILTER chain. When the target viewport HAD a clip this
      // genuinely mutates, so undo must restore the removed boundary — a
      // full-snapshot restore of the pre-mutation viewport (@codex #496 P2). For
      // an already-unclipped viewport the snapshot restore is a harmless verbatim
      // no-op (the forward reports mutated:true but left the document unchanged).
      const cmd = command as unknown as {
        type: "geometry/viewport-clip-remove";
        source: CommandSource;
        viewportId: string;
      };
      return buildViewportClipSnapshotInverse(doc, cmd.viewportId, cmd.source);
    }
    case "geometry/viewport-set-scale": {
      const cmd = command as unknown as {
        type: "geometry/viewport-set-scale";
        source: CommandSource;
        viewportId: string;
        scaleNum: number;
        scaleDenom: number;
      };
      const vp = findViewportInDoc(doc, cmd.viewportId);
      if (!vp) return null;
      const priorScale = readViewportOverrides(vp).scale;
      return {
        ...cmd,
        scaleNum: priorScale.num,
        scaleDenom: priorScale.denom,
      } as unknown as EditorCommand;
    }
    case "geometry/viewport-set-view": {
      // MC4 — undo of a pan/zoom view change. Snapshot the PRE-mutation
      // viewCenter + viewHeight from the pre-mutation document and emit a
      // set-view that restores them.
      //
      // The displayed scale is NOT re-derivable on undo: re-applying the
      // pre-mutation viewHeight makes applyViewportSetView compute
      // {1, round(viewHeight/height)} LOSSILY, which destroys a custom rational
      // (2:3 from MVSETUP, or any non-integer ratio). It also has TWO INDEPENDENT
      // storage carriers that can DIVERGE — the typed `vpOverrideScale` field AND
      // the EDIT_DWG_VP_OVERRIDES XDATA scale (applyViewportSetLock /
      // applyViewportVplayer persist the XDATA scale WITHOUT the typed field) — so
      // a faithful inverse must snapshot the pre-mutation value of BOTH and the
      // forward restores EACH exactly, never inferring one from the other
      // (@codex #419). Mirroring the shade-plot inverse's verbatim `prior`:
      //   • restoreVpOverrideScale = pre-mutation typed field (null = absent),
      //   • restoreXdataScale      = pre-mutation on-file XDATA scale (null = none).
      const cmd = command as unknown as {
        type: "geometry/viewport-set-view";
        source: CommandSource;
        viewportId: string;
        viewCenter?: { x: number; y: number };
        viewHeight?: number;
      };
      const vp = findViewportInDoc(doc, cmd.viewportId);
      if (!vp) return null;
      // Pure on-file XDATA evidence (NOT overridden by the typed field) — the
      // pre-mutation VP_OVERRIDES scale, or null when the block carries no scale.
      const preXdataScale = readViewportOverrides(vp).scaleFromXdata;
      return {
        type: "geometry/viewport-set-view",
        source: cmd.source,
        viewportId: cmd.viewportId,
        // Restore both fields the forward command could have touched. We snapshot
        // BOTH (not just the ones in the forward cmd) so a pan-only forward whose
        // applied value differed still round-trips, and so combined changes undo
        // atomically.
        viewCenter: { x: vp.viewCenter.x, y: vp.viewCenter.y },
        viewHeight: vp.viewHeight,
        // Preserve-source: exact pre-mutation typed scale (null = absent).
        restoreVpOverrideScale: vp.vpOverrideScale
          ? { num: vp.vpOverrideScale.num, denom: vp.vpOverrideScale.denom }
          : null,
        // Preserve-source: exact pre-mutation XDATA scale (null = none), snapshotted
        // independently so a lock/freeze-persisted scale survives undo of a pan/zoom.
        restoreXdataScale: preXdataScale
          ? { num: preXdataScale.num, denom: preXdataScale.denom }
          : null,
      } as unknown as EditorCommand;
    }
  }

  // ── Phase 4.1.e: property command inverses ───────────────────────────────
  // Common object property label → DrawingEntityBase field name.
  // Only these fields are read from the pre-mutation DrawingDocument to
  // snapshot old values. Entity-specific properties (Dimension style,
  // Contents, etc.) are deferred to Phase 4.1.f.
  const COMMON_PROP_FIELD: Record<string, string> = {
    Layer: "layer",
    Color: "color",
    Linetype: "linetype",
    Lineweight: "lineweight",
    Transparency: "transparency",
    // DXF group 48 (base schema, every type) + group 39 (per-type). These reach
    // the document via `propertyTranslator.commonLabelToEntityField` /
    // `mergeCommonFieldsForType`; the large-drawing `computeInverse` fallback
    // (used when the shell snapshot is empty) must snapshot them too or an undo
    // of a Thickness / Linetype Scale edit on a streamed drawing is a no-op.
    "Linetype Scale": "linetypeScale",
    Thickness: "thickness",
  };

  // Helper: read common object property values from a DrawingEntity. Numeric
  // typed fields (linetypeScale / thickness) are projected to their string form
  // so the inverse `properties/apply` command (which carries string values) can
  // restore them via the same translator coercion.
  //
  // Codex P2-1 (PR #403) — DEFAULT-RESTORE for the OPTIONAL numeric common
  // fields. When the prior value is the schema DEFAULT it is stored as
  // `undefined` (omitted) on the entity, NOT a number: `linetypeScale` is
  // `z.number().optional()` for EVERY type (AutoCAD default 1.0, DXF $LTSCALE /
  // group 48), and `thickness` is `z.number().optional()` on Dimension (group
  // 39 default 0). A bare `typeof value === "number"` guard SKIPPED the key for
  // a default→N forward, so `buildPropertyInverse` produced an inverse with NO
  // value for it → undo of a default→N change was a NO-OP (the new value lingered
  // in the document). For a default value we emit the canonical DEFAULT STRING so
  // the inverse restores it: linetypeScale → "1", thickness → "0" (thickness only
  // for a schema thickness-bearing type, so a non-bearing type — MText / Block /
  // etc. — mints no stray Thickness key). `thickness` defaults to 0 on every
  // bearing type whether declared `.default(0)` or `.optional()`, so "0" is the
  // correct restore for an omitted thickness on any bearing type.
  function readCommonProps(
    entity: DrawingEntity,
    keys: string[],
  ): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key of keys) {
      const field = COMMON_PROP_FIELD[key];
      if (!field) continue;
      // EPIC-COLOR-UNION S1 — `color` is the typed `EntityColor` union; project
      // it to the legacy string the inverse `properties/apply` command carries
      // (the forward command also uses the legacy-string `Color` value). The
      // other common fields (layer/linetype/lineweight/transparency) stay
      // strings on the entity and pass through verbatim.
      if (field === "color") {
        // EPIC-COLOR-UNION S1 — the inverse `properties/apply` snapshot must
        // carry the OLD colour LOSSLESSLY. `colorToLegacyString` collapses a
        // rich colour (`rgb` with a coexisting aci/name, or `bookColor`) to
        // "#rrggbb"/"ByLayer", so a colour edit followed by undo could not
        // reconstruct the 62/430 siblings and would corrupt the round-trip
        // state. `serializeColor` is lossless: it emits the EXACT legacy string
        // for the common cases (byLayer/byBlock/index/plain rgb — so the inverse
        // stays a plain value) and a `!EC:` payload for the rich ones; the apply
        // path (`commonFieldPatchValue`) restores it via `deserializeColor`.
        // `coerceColor` first defends a raw-string `color` that bypassed the
        // schema (`assembleDrawingDocument` does NOT re-parse in PROD).
        result[key] = serializeColor(coerceColor(entity.color));
        continue;
      }
      const value = (entity as unknown as Record<string, unknown>)[field];
      if (typeof value === "string") {
        result[key] = value;
      } else if (typeof value === "number" && Number.isFinite(value)) {
        // linetypeScale / thickness — project the numeric typed field to its
        // string form for the inverse `properties/apply` (string-valued).
        result[key] = String(value);
      } else if (value === undefined) {
        // Codex P2-1/P2-2 (PR #403) — the optional numeric field is at its schema
        // DEFAULT (omitted). Emit the canonical default STRING so the inverse
        // restores it (otherwise a default→N undo is a no-op). Color + the four
        // string fields are untouched (Color handled above; an undefined
        // layer/linetype/lineweight/transparency is not expected and stays
        // omitted). Delegates to the SHARED `optionalCommonKeyDefault` so this
        // (large-drawing computeInverse) path and the LIVE snapshot inverse agree
        // on the absent→default mapping — thickness "0" minted only for a schema
        // thickness-bearing type, linetypeScale "1" for every type.
        const def = optionalCommonKeyDefault(key, entity.type);
        if (def !== undefined) {
          result[key] = def;
        }
      }
    }
    return result;
  }

  // Helper: build a properties/apply inverse given per-entity old-value snapshots.
  // Returns homogeneous (single values map) if all entities had the same values,
  // or heterogeneous (perEntityValues) otherwise. Returns null for no-op (all
  // entities already had the new values — i.e. the forward was a no-op).
  function buildPropertyInverse(
    entityIds: string[],
    perEntityOld: Record<string, Record<string, string>>,
    newValues: Record<string, string>,
  ): EditorCommand | null {
    const validIds = entityIds.filter((id) => id in perEntityOld);
    if (validIds.length === 0) return null;

    // No-op check: every entity's old value matches the new value for all changed keys.
    const changedKeys = Object.keys(newValues);
    const allNoOp = validIds.every((id) => {
      const old = perEntityOld[id]!;
      return changedKeys.every(
        (key) => key in old && old[key] === newValues[key],
      );
    });
    if (allNoOp) return null;

    // Homogeneity check: all entities had the same old values.
    const first = perEntityOld[validIds[0]!]!;
    const isHomogeneous = validIds.slice(1).every((id) => {
      const old = perEntityOld[id]!;
      return (
        Object.keys(first).length === Object.keys(old).length &&
        Object.entries(first).every(([k, v]) => old[k] === v)
      );
    });

    if (isHomogeneous) {
      return {
        type: "properties/apply",
        source: "system",
        entityIds: validIds,
        values: first,
      } as unknown as EditorCommand;
    }

    return {
      type: "properties/apply",
      source: "system",
      entityIds: validIds,
      values: {},
      perEntityValues: perEntityOld,
    } as unknown as EditorCommand;
  }

  // properties/apply — called with pre-mutation doc (editorStore passes preMutationDoc
  // for this command type so old values are readable here, not the new values).
  type PropertiesApplyCmd = {
    type: "properties/apply";
    source: CommandSource;
    entityIds: string[];
    values: Record<string, string>;
  };
  const asPropertiesApply = command as unknown as PropertiesApplyCmd;
  if (asPropertiesApply.type === "properties/apply") {
    const { entityIds, values } = asPropertiesApply;
    // Only snapshot the common object property keys that are present in values.
    const knownKeys = Object.keys(values).filter((k) => k in COMMON_PROP_FIELD);
    if (knownKeys.length === 0 || entityIds.length === 0) return null;

    const perEntityOld: Record<string, Record<string, string>> = {};
    for (const entityId of entityIds) {
      const entity = findEntityById(doc, entityId);
      if (!entity) continue;
      perEntityOld[entityId] = readCommonProps(entity, knownKeys);
    }

    return buildPropertyInverse(entityIds, perEntityOld, values);
  }

  // chprop/apply — geometry document is NOT updated by this command (shell-only),
  // so doc (nextGeometryDocument) already contains the old entity field values.
  type ChpropApplyCmd = {
    type: "chprop/apply";
    source: CommandSource;
    entityIds: string[];
    property: string;
    value: string;
  };
  const asChprop = command as unknown as ChpropApplyCmd;
  if (asChprop.type === "chprop/apply") {
    const normalizedProperty = asChprop.property.replace(/_/g, " ");
    if (!(normalizedProperty in COMMON_PROP_FIELD)) return null;

    const perEntityOld: Record<string, Record<string, string>> = {};
    for (const entityId of asChprop.entityIds) {
      const entity = findEntityById(doc, entityId);
      if (!entity) continue;
      perEntityOld[entityId] = readCommonProps(entity, [normalizedProperty]);
    }

    return buildPropertyInverse(
      asChprop.entityIds,
      perEntityOld,
      { [normalizedProperty]: asChprop.value },
    );
  }

  // matchprop/apply — geometry document is NOT updated by this command (shell-only),
  // so doc contains the old property values for all target entities.
  type MatchpropApplyCmd = {
    type: "matchprop/apply";
    source: CommandSource;
    targetEntityIds: string[];
    mode: string;
  };
  const asMatchprop = command as unknown as MatchpropApplyCmd;
  if (asMatchprop.type === "matchprop/apply") {
    const { targetEntityIds } = asMatchprop;
    if (targetEntityIds.length === 0) return null;

    const perEntityOld: Record<string, Record<string, string>> = {};
    const allCommonKeys = Object.keys(COMMON_PROP_FIELD);
    for (const entityId of targetEntityIds) {
      const entity = findEntityById(doc, entityId);
      if (!entity) continue;
      perEntityOld[entityId] = readCommonProps(entity, allCommonKeys);
    }

    // For matchprop inverse, "new values" aren't known without running the
    // forward (source entity's properties). Use empty map — buildPropertyInverse
    // will always produce a non-null result since old values differ from {}
    // (the no-op check passes: {} has no keys, so allNoOp = true vacuously).
    // Special-case: check that at least one entity was found.
    const validIds = targetEntityIds.filter((id) => id in perEntityOld);
    if (validIds.length === 0) return null;

    // Build per-entity restore: each target gets its own old common-prop snapshot.
    const first = perEntityOld[validIds[0]!]!;
    const isHomogeneous = validIds.slice(1).every((id) => {
      const old = perEntityOld[id]!;
      return (
        Object.keys(first).length === Object.keys(old).length &&
        Object.entries(first).every(([k, v]) => old[k] === v)
      );
    });

    if (isHomogeneous) {
      return {
        type: "properties/apply",
        source: "system",
        entityIds: validIds,
        values: first,
      } as unknown as EditorCommand;
    }

    return {
      type: "properties/apply",
      source: "system",
      entityIds: validIds,
      values: {},
      perEntityValues: perEntityOld,
    } as unknown as EditorCommand;
  }

  switch (command.type) {
    // FF-4 + HIST-{MTEXT,HATCH,MLEADER,REVCLOUD}-INVERSE — every single-append
    // create shares one inverse: delete the entity the forward just minted.
    // `doc` here is the POST-mutation document (editorStore passes
    // nextGeometryDocument for creates) so the last-of-type fallback sees the
    // newly-minted entity; the mutations payload, when threaded, pins the
    // exact id even when same-type entities pre-exist. create-dimension mints
    // NO anonymous *D block in this model (the reducer appends a single
    // Dimension entity), so a plain delete fully reverses it.
    // REVCLOUD-MODES / HIST-REVCLOUD-INVERSE — Object-mode REVCLOUD
    // REPLACES the picked closed source entity with the cloud (AutoCAD
    // deletes the source by default), so its inverse must BOTH delete the
    // minted cloud AND restore the source snapshot. `doc` here is the
    // PRE-mutation document (editorStore needsPreMutationDoc gates on
    // creationMode === "Object") so the source can be snapshotted
    // verbatim; the minted cloud id comes from mutations.added. A forward
    // that no-opped (source missing / not a closed outline) diffs to an
    // empty added set — return null so no history entry is pushed.
    case "geometry/create-revcloud": {
      if (command.creationMode === "Object") {
        if (!mutations || mutations.added.length === 0) return null;
        const cloudIds = mutations.added.map((a) => a.entity.id);
        const sourceEntity = command.sourceEntityId
          ? findEntityById(doc, command.sourceEntityId)
          : undefined;
        if (!sourceEntity) {
          // Source vanished from the pre-doc (defensive) — fall back to a
          // plain delete of the minted cloud so undo at least removes it.
          return {
            type: "shell/delete-entities",
            source: command.source,
            entityIds: cloudIds,
          } as unknown as EditorCommand;
        }
        const sourceSpaceId =
          findEntitySpaceId(doc, sourceEntity.id) ?? sourceEntity.spaceId;
        return buildArrayDeleteRestoreInverse(
          cloudIds,
          [cloneEntity(sourceEntity)],
          sourceSpaceId,
        );
      }
      // Rectangular / Polygonal / Freehand — plain single-append create.
      // editorStore gates needsPreMutationDoc on creationMode === "Object"
      // only, so for these modes `doc` is still the POST-mutation document
      // and the legacy last-of-type fallback keeps working unchanged.
      return inverseForSingleEntityCreate(
        doc,
        command.source,
        command.spaceId,
        SINGLE_CREATE_ENTITY_TYPE[command.type],
        mutations,
      );
    }
    case "geometry/create-line":
    case "geometry/create-point":
    case "geometry/create-circle":
    case "geometry/create-arc":
    case "geometry/create-ellipse":
    case "geometry/create-spline":
    case "geometry/create-lwpolyline":
    case "geometry/create-donut":
    case "geometry/create-text":
    case "geometry/create-mtext":
    case "geometry/create-dimension":
    case "geometry/create-multileader":
    case "geometry/create-wipeout":
    case "geometry/create-hatch":
    case "geometry/create-viewport": {
      return inverseForSingleEntityCreate(
        doc,
        command.source,
        command.spaceId,
        SINGLE_CREATE_ENTITY_TYPE[command.type],
        mutations,
      );
    }
    // FF-1 — ARRAY (rect/polar/path). The forward TAGS every source entity in
    // place (arrayGroupId + arraySourceOfGroup + arrayMode, overwriting any
    // prior tags from an earlier group) and APPENDS the copy clones. `doc`
    // here is the PRE-mutation document (editorStore needsPreMutationDoc) so
    // the sources' pre-tag snapshots can be restored verbatim; the minted
    // copy ids come from mutations.added. Inverse = delete the copies + the
    // (re-tagged) sources, then restore the pre-mutation source snapshots.
    //
    // mutations gates the no-op forwards: MAXARRAY / missing sources /
    // foreign-groupId collision all return the document unchanged, which
    // diffs to an empty mutation set — null so an idle ARRAY pushes no
    // history entry. A 1×1 ARRAYRECT mints no copies but still tags the
    // sources (added=[] / updated=[sources]) — the inverse must still
    // restore the untagged sources.
    case "geometry/create-arrayrect":
    case "geometry/create-arraypolar":
    case "geometry/create-arraypath": {
      if (!mutations) return null;
      const copyIds = mutations.added.map((a) => a.entity.id);
      const sourcesTouched = (mutations.updated ?? []).length > 0;
      if (copyIds.length === 0 && !sourcesTouched) return null;

      const sourceIds: string[] = [];
      const sourceSnapshots: DrawingEntity[] = [];
      let spaceId: string | undefined;
      // FU2 (#305) — the contract allows repeated ids (plain z.array, no
      // uniqueness); snapshot once per UNIQUE id (Set preserves the
      // first-occurrence order, mirroring the arrayedit dedup below) —
      // duplicate snapshots would be blind-appended by shell/restore-entities
      // and leave two same-id entities after undo.
      for (const id of new Set(command.entityIds)) {
        const entity = findEntityById(doc, id);
        if (!entity) continue; // forward's collectSourceEntities skipped it too
        sourceIds.push(id);
        sourceSnapshots.push(cloneEntity(entity));
        if (!spaceId) spaceId = findEntitySpaceId(doc, id) ?? entity.spaceId;
      }
      if (sourceSnapshots.length === 0) return null;

      return buildArrayDeleteRestoreInverse(
        [...copyIds, ...sourceIds],
        sourceSnapshots,
        spaceId ?? sourceSnapshots[0]!.spaceId,
      );
    }
    // FF-2 — ARRAYEDIT. The forward deletes every COPY of the group (members
    // tagged arrayGroupId without the arraySourceOfGroup marker), re-tags the
    // surviving sources (the arrayMode tag can CHANGE on a rect→polar edit)
    // and regenerates the copies with the new parameters. Regenerated copies
    // can REUSE freed copy ids (nextGeometryEntityId scans the copies-removed
    // document), so the post-mutation member set ⊆ pre-mutation member ids ∪
    // mutations.added ids. `doc` is the PRE-mutation document — snapshot ALL
    // members (sources + old copies), delete that union, restore the
    // snapshots.
    case "geometry/arrayedit": {
      if (!mutations) return null;
      const newCopyIds = mutations.added.map((a) => a.entity.id);
      const forwardChangedDoc =
        newCopyIds.length > 0 ||
        (mutations.removed ?? []).length > 0 ||
        (mutations.updated ?? []).length > 0;
      // Forward no-opped (unknown group / missing mode params) — no entry.
      if (!forwardChangedDoc) return null;

      const memberIds: string[] = [];
      const memberSnapshots: DrawingEntity[] = [];
      let spaceId: string | undefined;
      for (const space of doc.spaces) {
        for (const entity of space.entities) {
          if (entity.arrayGroupId !== command.arrayGroupId) continue;
          memberIds.push(entity.id);
          memberSnapshots.push(cloneEntity(entity));
          if (!spaceId) spaceId = space.id;
        }
      }
      if (memberSnapshots.length === 0) return null;

      // De-dup: a regenerated copy that reused a freed id is already in
      // memberIds; deleting a since-removed old-copy id is a reducer no-op.
      const deleteIds = [...new Set([...memberIds, ...newCopyIds])];
      return buildArrayDeleteRestoreInverse(
        deleteIds,
        memberSnapshots,
        spaceId ?? memberSnapshots[0]!.spaceId,
      );
    }
    // HATCHEDIT — the forward overwrites the target hatch's pattern/scale/angle/
    // style/patternType/associative fields IN PLACE (no add/remove). The inverse
    // re-applies the PRE-mutation value of EXACTLY the fields the forward CHANGED
    // (read from `doc`, the pre-mutation document — hatchedit is in editorStore
    // `needsPreMutationDoc`), emitting a restoring `geometry/hatchedit`. Mirrors
    // the `properties/apply` "snapshot whatever this command mutated" inverse:
    // diff per field, restore only the changed ones, return null on a no-op.
    case "geometry/hatchedit": {
      // Codex round-4 #3 — narrow `cmd` to ALL NINE typed hatch fields (not just
      // the 6 pattern/scale/angle/style/type/associative ones). A typed call
      // changing ONLY solidFill, or only gradient / customPatternLines, was
      // previously un-inverted: the inverse only diffed when patternName changed.
      // Diffing every supplied field makes the inverse complete for any subset.
      const cmd = command as unknown as {
        type: "geometry/hatchedit";
        source: CommandSource;
        entityId: string;
        patternName?: string;
        patternScale?: number;
        patternAngle?: number;
        hatchStyle?: number;
        patternType?: number;
        associative?: boolean;
        solidFill?: boolean;
        gradient?: unknown;
        customPatternLines?: unknown;
        // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — DXF 77.
        doubleLine?: boolean;
      };
      const target = findEntityById(doc, cmd.entityId);
      // Unknown id / wrong type → the forward no-opped, so does the inverse.
      if (!target || target.type !== "Hatch") return null;
      const hatch = target as unknown as {
        patternName?: string;
        patternScale?: number;
        patternAngle?: number;
        hatchStyle?: number;
        patternType?: number;
        associative?: boolean;
        solidFill?: boolean;
        gradient?: unknown;
        customPatternLines?: unknown;
        doubleLine?: boolean;
      };
      // Restore ONLY the fields the forward actually set AND whose value differs
      // from the pre-mutation hatch (an unchanged field is a no-op the forward
      // wrote nothing for). Read the PRIOR value verbatim from the document so
      // full precision survives (P2-D: patternScale/patternAngle are raw numbers
      // on the typed entity — no preview-string rounding here).
      const inverse: {
        type: "geometry/hatchedit";
        source: CommandSource;
        entityId: string;
        patternName?: string;
        patternScale?: number;
        patternAngle?: number;
        hatchStyle?: number;
        patternType?: number;
        associative?: boolean;
        solidFill?: boolean;
        gradient?: unknown;
        customPatternLines?: unknown;
        doubleLine?: boolean;
      } = {
        type: "geometry/hatchedit",
        source: "system",
        entityId: cmd.entityId,
      };
      let changed = false;
      // `null` is the explicit "restore to absent" signal (vs omitted =
      // untouched), so an undo of "set gradient on a plain hatch" actively
      // clears it again rather than leaving a redo's gradient stranded.
      const jsonEq = (a: unknown, b: unknown): boolean =>
        JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
      if (cmd.patternName !== undefined && cmd.patternName !== hatch.patternName) {
        inverse.patternName = hatch.patternName ?? "ANSI31";
        // The forward translator DERIVES solidFill (DXF 70) from the new pattern
        // name (SOLID ⇒ true) and CLEARS gradient / customPatternLines on a
        // pattern-CLASS change (they cannot be re-derived from the new name).
        // Capture the PRIOR payloads VERBATIM alongside the restored patternName
        // so undo restores them EXACTLY — even for an imported hatch whose source
        // solidFill did not match its patternName's SOLID-ness (re-deriving on
        // undo would diverge from the captured source). Read straight from the
        // pre-mutation document (the "preserve source, never infer" round-trip
        // rule). These are forced even when the command did NOT supply them,
        // because the forward changes them as a SIDE EFFECT of the name change.
        if (inverse.solidFill === undefined) {
          inverse.solidFill = hatch.solidFill ?? false;
        }
        if (inverse.gradient === undefined) {
          inverse.gradient = hatch.gradient ?? null;
        }
        if (inverse.customPatternLines === undefined) {
          inverse.customPatternLines = hatch.customPatternLines ?? null;
        }
        changed = true;
      }
      if (
        cmd.patternScale !== undefined &&
        cmd.patternScale !== hatch.patternScale
      ) {
        inverse.patternScale = hatch.patternScale ?? 1;
        changed = true;
      }
      if (
        cmd.patternAngle !== undefined &&
        cmd.patternAngle !== hatch.patternAngle
      ) {
        inverse.patternAngle = hatch.patternAngle ?? 0;
        changed = true;
      }
      if (cmd.hatchStyle !== undefined && cmd.hatchStyle !== hatch.hatchStyle) {
        inverse.hatchStyle = hatch.hatchStyle ?? 0;
        changed = true;
      }
      if (
        cmd.patternType !== undefined &&
        cmd.patternType !== hatch.patternType
      ) {
        inverse.patternType = hatch.patternType ?? 1;
        changed = true;
      }
      if (
        cmd.associative !== undefined &&
        cmd.associative !== hatch.associative
      ) {
        inverse.associative = hatch.associative ?? true;
        changed = true;
      }
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — DXF 77,
      // diffed exactly like hatchStyle/patternType/associative above.
      if (
        cmd.doubleLine !== undefined &&
        cmd.doubleLine !== hatch.doubleLine
      ) {
        inverse.doubleLine = hatch.doubleLine ?? false;
        changed = true;
      }
      // Codex round-4 #3 — diff solidFill / gradient / customPatternLines on
      // their OWN, so a typed call that changes ONLY one of them (without a
      // patternName change) is inverted. A `null`/absent prior restores to
      // absent; a real prior payload is restored verbatim. Object fields are
      // compared by VALUE (JSON), not identity. Guard `inverse.* === undefined`
      // so a patternName side-effect capture above is not double-written.
      if (cmd.solidFill !== undefined && cmd.solidFill !== hatch.solidFill) {
        if (inverse.solidFill === undefined) {
          inverse.solidFill = hatch.solidFill ?? false;
        }
        changed = true;
      }
      if ("gradient" in cmd && !jsonEq(cmd.gradient, hatch.gradient)) {
        if (inverse.gradient === undefined) {
          inverse.gradient = hatch.gradient ?? null;
        }
        changed = true;
      }
      if (
        "customPatternLines" in cmd &&
        !jsonEq(cmd.customPatternLines, hatch.customPatternLines)
      ) {
        if (inverse.customPatternLines === undefined) {
          inverse.customPatternLines = hatch.customPatternLines ?? null;
        }
        changed = true;
      }
      // No field actually changed → nothing to undo.
      if (!changed) return null;
      return inverse as unknown as EditorCommand;
    }
    case "geometry/create-block-insert":
    case "geometry/create-block-inserts-batch": {
      // INSERT / MINSERT appends one Block (INSERT) entity per dispatch (an AI
      // MINSERT fans out to N create-block-insert commands, each its own
      // dispatch — OR, since T6b, ONE create-block-inserts-batch dispatch
      // appending all N in a single reducer pass). Inverse = delete the
      // appended insert(s) — same mutations.added pattern as geometry/copy /
      // offset / mirror-copy, and identical either way since it is driven
      // entirely by `mutations.added`, not the command shape. The referenced
      // block DEFINITION is untouched (an INSERT of a pre-existing block must
      // NOT remove the def on undo — only the insert(s)).
      if (!mutations || mutations.added.length === 0) return null;
      const insertIds = mutations.added.map((a) => a.entity.id);
      return {
        type: "shell/delete-entities",
        source: "system",
        entityIds: insertIds,
      } as unknown as EditorCommand;
    }
    case "geometry/create-block-definition": {
      // The forward (createBlockDefinitionInDocument) registers blocks[name] +
      // blockRecords[name] and, per postAction, removes the source entities
      // (delete/convert) and appends a convert INSERT (convert). The inverse
      // reverses ALL of that. `doc` here is the PRE-mutation document
      // (create-block-definition is in editorStore needsPreMutationDoc), so the
      // removed source entities can be snapshotted for restore.
      const cmd = command as unknown as {
        type: "geometry/create-block-definition";
        source: CommandSource;
        blockDef: { name: string };
        postAction?: "retain" | "convert" | "delete";
        sourceEntityIds?: string[];
        convertInsertId?: string;
      };
      const blockName = cmd.blockDef?.name;
      if (!blockName) return null;
      // The forward rejects a duplicate name (validateBlockName), so a SUCCESSFUL
      // create means the block did not pre-exist. Guard defensively against the
      // impossible "name already present in pre-mutation doc" case so undo never
      // clobbers a block it did not create.
      const preExisted = doc.blocks?.[blockName] !== undefined;

      // Reverse order of the forward: (1) delete the convert INSERT, (2) restore
      // removed source entities, (3) remove the newly-registered block def.
      const subInverses: EditorCommand[] = [];

      if (cmd.postAction === "convert" && cmd.convertInsertId) {
        subInverses.push({
          type: "shell/delete-entities",
          source: "system",
          entityIds: [cmd.convertInsertId],
        } as unknown as EditorCommand);
      }

      if (
        (cmd.postAction === "delete" || cmd.postAction === "convert") &&
        cmd.sourceEntityIds &&
        cmd.sourceEntityIds.length > 0
      ) {
        const entities: DrawingEntity[] = [];
        let spaceId: string | undefined;
        // FU2 (#305 sweep) — dedupe: a repeated source id must snapshot once,
        // or the shell/restore-entities sub-inverse re-appends it twice.
        for (const id of new Set(cmd.sourceEntityIds)) {
          const entity = findEntityById(doc, id);
          if (entity) {
            entities.push(cloneEntity(entity));
            if (!spaceId) spaceId = findEntitySpaceId(doc, id) ?? entity.spaceId;
          }
        }
        if (entities.length > 0) {
          subInverses.push({
            type: "shell/restore-entities",
            source: "system",
            entities,
            spaceId: spaceId ?? entities[0]!.spaceId,
          } as unknown as EditorCommand);
        }
      }

      if (!preExisted) {
        subInverses.push({
          type: "geometry/remove-block-definition",
          source: "system",
          name: blockName,
        } as unknown as EditorCommand);
      }

      if (subInverses.length === 0) return null;
      if (subInverses.length === 1) return subInverses[0]!;
      // Multiple steps (delete/convert postActions): compose into a single
      // history-internal batch. replay-inverses dispatches each member in array
      // order with __bypassHistory, so this nests cleanly inside the outer AI /
      // history batch's replay-inverses (see editorStore history/replay-inverses).
      return {
        type: "history/replay-inverses",
        source: "system",
        batch: subInverses,
      } as unknown as EditorCommand;
    }
    // D4 — TRIM / EXTEND / FILLET / CHAMFER / BREAK / JOIN / EXPLODE-ENTITIES /
    // EXPLODE-INSERT / LENGTHEN / STRETCH. All ten share the SAME inverse shape
    // (see `buildModifyCommandSnapshotInverse`'s doc comment): the forward's
    // `ForwardMutations` diff (added/updated/removed) is complete enough to
    // reconstruct a delete+restore undo without any command-specific math.
    case "geometry/trim-entity":
    case "geometry/extend-entity":
    case "geometry/fillet-entities":
    case "geometry/chamfer-entities":
    case "geometry/break-entity":
    case "geometry/join-entities":
    case "geometry/explode-entities":
    case "geometry/explode-insert":
    case "geometry/lengthen-entity":
    case "geometry/stretch-entities":
      return buildModifyCommandSnapshotInverse(doc, mutations);
    default:
      return null;
  }
}
